
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { AdjustmentsIcon } from '@heroicons/react/outline';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import { toast } from 'sonner';
import type { Voice } from 'types';
import uuidv4 from '@/lib/uuid';
import Modal from '@/components/atoms/Modal';
import MultiFileUpload from '@/components/atoms/MultiFileUpload';
import Select from '@/components/atoms/Select';
import AuthModal from '@/components/AuthModal';
import NarrowDashboardLayout from '@/components/layout/NarrowDashboardLayout';
import Seo from '@/components/Seo';
import VoicesModal, { VOICE_PICKER_PANEL_CLASS, } from '@/components/speech/VoicesModal';
import { THEMES } from '@/components/video-editor/explainer/themes';
import type { CharacterColor, ThemeId, } from '@/components/video-editor/explainer/types';
import { ActorSelectorModal } from '@/components/videos/ActorSelectorModal';
import VideoAgentLanding from '@/components/videos/landing/VideoAgentLanding';
const PricingModal = dynamic(() => import('@/components/PricingModal'));
import { CAPTION_PRESET_OPTIONS, type CaptionPresetId, DEFAULT_CAPTION_PRESET, } from '@/constants/caption-presets';
import { DEFAULT_VOICE, VOICE_LIST_URL } from '@/constants/defaultVoice';
import { DEFAULT_EXPLAINER_LOOK, EXPLAINER_DURATION, EXPLAINER_LOOKS, EXPLAINER_SHORT_DURATION, EXPLAINER_SHORT_STYLES, explainerCredits, type ExplainerFormat, explainerGenerationMinutes, explainerLookHasSfx, explainerQuoteSeconds, type ExplainerShortStyle, formatExplainerDuration, isExplainerLanguage, isExplainerWorkflowEnabled, } from '@/constants/explainer';
import { LIBRARY_CATEGORIES, type LibraryCategory, type LibraryTrack, MUSIC_LIBRARY, } from '@/constants/music-library';
import { getVideoAgentPreset } from '@/constants/video-agent-presets';
import { DEFAULT_IMAGE_MODEL, DEFAULT_VIDEO_MODEL, IMAGE_MODELS, type ImageModelOption, VIDEO_GENERATION_PRESETS, VIDEO_MODELS, type VideoModelId, WORKFLOW_TO_MEDIA_TYPE, WORKFLOW_TYPES, type WorkflowType, } from '@/constants/video-generation-presets';
import { AUTO_VIDEO_LANGUAGE, VIDEO_LANGUAGE_OPTIONS, videoLanguageLabel, } from '@/constants/video-languages';
import { compressImage } from '@/utils/compressImage';
import { createClient as createSupabaseComponentClient } from '@/utils/supabase/component';
import { useUser } from '@/utils/useUser';
import { getVoiceImageUrl } from '@/utils/voices';
import type { Actor, CustomActor } from '@/types/actor';
// ── Types ─────────────────────────────────────────────────────────
type AspectRatio = '16:9' | '9:16' | '1:1';
// Inlined at build time; the Explainer card only shows when it's on.
const EXPLAINER_ENABLED = isExplainerWorkflowEnabled();
// Slider maximum for every other workflow (the API clamps to the same).
const DEFAULT_MAX_DURATION = 200;
// Style preview backgrounds are used behind thumbnails and as future-preset fallback.
const STYLE_PREVIEW: Record<string, {
    bg: string;
    overlay?: string;
    tone: 'light' | 'dark';
}> = {
    default: {
        bg: 'linear-gradient(135deg,#eef2ff 0%,#e0e7ff 50%,#c7d2fe 100%)',
        tone: 'light',
    },
    cinematic: {
        bg: 'radial-gradient(120% 80% at 80% 20%,#fb923c 0%,#9a3412 45%,#1c1917 100%)',
        overlay: 'linear-gradient(0deg,rgba(0,0,0,.45) 0%,transparent 25%,transparent 75%,rgba(0,0,0,.45) 100%)',
        tone: 'dark',
    },
    ghibli: {
        bg: 'linear-gradient(160deg,#dcfce7 0%,#bae6fd 55%,#fde68a 100%)',
        tone: 'light',
    },
    educational: {
        bg: 'linear-gradient(135deg,#ffffff 0%,#dbeafe 60%,#fde68a 100%)',
        tone: 'light',
    },
    pixar: {
        bg: 'radial-gradient(110% 80% at 30% 30%,#fcd34d 0%,#f97316 40%,#7c3aed 100%)',
        tone: 'dark',
    },
    anime: {
        bg: 'linear-gradient(135deg,#fbcfe8 0%,#a78bfa 55%,#22d3ee 100%)',
        tone: 'light',
    },
    watercolor: {
        bg: 'radial-gradient(80% 60% at 70% 30%,#fed7aa 0%,#ddd6fe 50%,#a7f3d0 100%)',
        tone: 'light',
    },
    comic: {
        bg: 'linear-gradient(135deg,#facc15 0%,#ef4444 60%,#0f172a 100%)',
        tone: 'dark',
    },
    minimalist: {
        bg: 'linear-gradient(135deg,#ffffff 0%,#f1f5f9 60%,#e2e8f0 100%)',
        tone: 'light',
    },
    retro: {
        bg: 'radial-gradient(100% 80% at 50% 100%,#fb7185 0%,#a855f7 50%,#1e1b4b 100%)',
        tone: 'dark',
    },
    'flat-animation': {
        bg: 'linear-gradient(135deg,#bfdbfe 0%,#86efac 55%,#fef3c7 100%)',
        tone: 'light',
    },
    'sketch-color': {
        bg: 'linear-gradient(135deg,#fef3c7 0%,#fed7aa 48%,#bfdbfe 100%)',
        tone: 'light',
    },
    'sketch-black-white': {
        bg: 'linear-gradient(135deg,#fafafa 0%,#d4d4d4 58%,#737373 100%)',
        tone: 'light',
    },
    'japanese-ink': {
        bg: 'radial-gradient(90% 70% at 65% 30%,#f5f5f4 0%,#d6d3d1 55%,#1c1917 100%)',
        tone: 'dark',
    },
    '3d': {
        bg: 'radial-gradient(100% 75% at 45% 25%,#fef08a 0%,#38bdf8 48%,#1e3a8a 100%)',
        tone: 'dark',
    },
    'low-poly': {
        bg: 'linear-gradient(135deg,#bae6fd 0%,#4ade80 50%,#334155 100%)',
        tone: 'dark',
    },
    lego: {
        bg: 'linear-gradient(135deg,#facc15 0%,#22c55e 45%,#2563eb 100%)',
        tone: 'dark',
    },
    'pixel-art': {
        bg: 'linear-gradient(135deg,#60a5fa 0%,#34d399 48%,#f97316 100%)',
        tone: 'dark',
    },
    manga: {
        bg: 'linear-gradient(135deg,#ffffff 0%,#e0f2fe 48%,#1e40af 100%)',
        tone: 'light',
    },
    'manga-black-white': {
        bg: 'linear-gradient(135deg,#ffffff 0%,#a3a3a3 48%,#171717 100%)',
        tone: 'dark',
    },
    'kids-book': {
        bg: 'linear-gradient(135deg,#fef3c7 0%,#bbf7d0 48%,#93c5fd 100%)',
        tone: 'light',
    },
    minecraft: {
        bg: 'linear-gradient(135deg,#86efac 0%,#22c55e 42%,#92400e 100%)',
        tone: 'dark',
    },
    claymation: {
        bg: 'radial-gradient(90% 70% at 50% 25%,#fed7aa 0%,#fb923c 48%,#57534e 100%)',
        tone: 'dark',
    },
    cyberpunk: {
        bg: 'radial-gradient(95% 75% at 65% 25%,#22d3ee 0%,#d946ef 45%,#0f172a 100%)',
        tone: 'dark',
    },
    isometric: {
        bg: 'linear-gradient(135deg,#cffafe 0%,#86efac 48%,#fef08a 100%)',
        tone: 'light',
    },
    'film-noir': {
        bg: 'radial-gradient(90% 75% at 65% 25%,#f5f5f5 0%,#525252 50%,#0a0a0a 100%)',
        tone: 'dark',
    },
    'charcoal-drawing': {
        bg: 'linear-gradient(135deg,#f5f5f4 0%,#a8a29e 48%,#292524 100%)',
        tone: 'dark',
    },
};
// Example prompts for the inline "Surprise me" button. The button only seeds
// the textarea — it doesn't touch workflow / style / music so the user keeps
// whatever they've already chosen.
const EXAMPLE_PROMPTS: string[] = [
    'A 30-second product launch for a smart water bottle that tracks hydration, with sleek minimalist visuals and confident pacing.',
    'A 60-second educational explainer about how black holes form, using clear analogies and simple diagrams.',
    'A cinematic travel reel for a weekend in Lisbon: tile-lined streets, sunset miradouros, pastéis de nata.',
    'A cozy bedtime story about a curious cat learning to bake sourdough on a rainy afternoon.',
    'A 30-second TikTok on why most people procrastinate, told as a relatable personal story with a surprising twist.',
    'A property listing tour of a modern Singapore apartment, highlighting the skyline view and design details.',
    'A 45-second Instagram ad for a small-batch coffee roastery, showing the journey from bean to cup.',
    'An explainer on why property prices in Singapore keep rising, mixing news clips with simple charts.',
];
// Eight popular styles surfaced before "Show all 27" opens the rest. Order
// matches what most users start with (realism, then a few illustration
// directions, then a couple of stylised picks).
const POPULAR_STYLE_IDS = new Set([
    'default',
    'cinematic',
    'pixar',
    'anime',
    'watercolor',
    'ghibli',
    'educational',
    'comic',
]);
// Cap for prompt-box reference images (photos of the real subject — e.g. a
// condo development for a realtor's launch video). Kept below the ~10-image
// i2i model limit so selected character photos still fit alongside them.
const MAX_REFERENCE_IMAGES = 6;
interface ReferenceImage {
    id: string;
    url: string;
    path: string;
    name: string;
}
// ── Page Component ────────────────────────────────────────────────
/**
 * A catalog narrator whose own language is `label` ("Japanese"): an
 * educational or storytelling voice first, in the catalog's order.
 */
function nativeNarrator(voices: Voice[], label: string): Voice | undefined {
    const rank = (v: Voice) => v.use_case === 'informative_educational'
        ? 0
        : v.use_case === 'narrative_story'
            ? 1
            : 2;
    return voices
        .filter((v) => !v.user_id &&
        (Array.isArray(v.language) ? v.language : [v.language]).includes(label))
        .sort((a, b) => rank(a) - rank(b))[0];
}
export default function CreateVideoPage() {
    const router = useRouter();
    const { user, subscription, isFreePlan, isLoading } = useUser();
    // Free plan can generate image-based videos (image story, stock footage,
    // your-own-media stills); AI-video workflows open the pricing modal.
    // Never-subscribed users have NO subscriptions row (isFreePlan only
    // matches an explicit 'Free' product), so treat a signed-in user without
    // a subscription as free too. While the subscription is still loading we
    // assume paid so paid users never see a lock flash.
    const isFreeTier = !!user && !isLoading && (isFreePlan || !subscription);
    const [shouldUpgrade, setShouldUpgrade] = useState(false);
    // Form state
    const [prompt, setPrompt] = useState('');
    const [aspectRatio, setAspectRatio] = useState<AspectRatio>('9:16');
    const [duration, setDuration] = useState(30);
    const [autoDuration, setAutoDuration] = useState(false);
    const [workflowType, setWorkflowType] = useState<WorkflowType>('image-story');
    const [preset, setPreset] = useState('default');
    const [voiceId, setVoiceId] = useState(DEFAULT_VOICE.voice_id);
    // Free-form Suno prompt. Default is empty so the textarea reads as a blank
    // slate — when the user hits Generate without typing, the API falls back
    // to a sensible "uplifting pop" style so we never send Suno an empty
    // string.
    const [musicPrompt, setMusicPrompt] = useState('');
    // Music source decouples the picker tab from the actual track. 'library'
    // hands a pre-uploaded MP3 to the backend (skips Suno generation, faster
    // and cheaper). 'ai' keeps the existing generated-track flow. 'none' is the
    // user-opted-out silent video.
    const [musicSource, setMusicSource] = useState<'auto' | 'library' | 'ai' | 'none'>('library');
    const [libraryTrackId, setLibraryTrackId] = useState<string>(MUSIC_LIBRARY[0]?.id || '');
    const [libraryCategory, setLibraryCategory] = useState<LibraryCategory | 'all'>('all');
    // Multi-character: image models accept multiple reference photos. The user
    // picks any number of their saved actors and the backend passes every
    // photo to i2i. First-selected actor is treated as the "primary" (e.g.
    // talking-head narrator on video-story / talking-video).
    const [actorIds, setActorIds] = useState<string[]>([]);
    const [videoModel, setVideoModel] = useState<VideoModelId>(DEFAULT_VIDEO_MODEL);
    const [imageModel, setImageModel] = useState<ImageModelOption>(DEFAULT_IMAGE_MODEL);
    const [captionPreset, setCaptionPreset] = useState<CaptionPresetId>(DEFAULT_CAPTION_PRESET);
    const [captionsEnabled, setCaptionsEnabled] = useState(true);
    // Spoken-layer language. 'auto' lets the script director follow the
    // prompt; an ISO-639-1 code localizes narration, voiceover and captions.
    const [language, setLanguage] = useState<string>(AUTO_VIDEO_LANGUAGE);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [customActors, setCustomActors] = useState<CustomActor[]>([]);
    const [showActorModal, setShowActorModal] = useState(false);
    const [showVoiceModal, setShowVoiceModal] = useState(false);
    const [modalVoices, setModalVoices] = useState<Voice[]>([DEFAULT_VOICE]);
    const [modalVoicesLoading, setModalVoicesLoading] = useState(true);
    const [showAllStyles, setShowAllStyles] = useState(false);
    // Explainer look (EXPLAINER_LOOKS); the looks with sound effects can turn
    // them off.
    const [explainerLook, setExplainerLook] = useState<ThemeId>(DEFAULT_EXPLAINER_LOOK);
    const [explainerSfx, setExplainerSfx] = useState(true);
    // The look card under the pointer plays a few seconds of its look.
    const [hoveredLook, setHoveredLook] = useState<ThemeId | null>(null);
    // An Explainer is a video or a Short: vertical, 20-60 s, built around a
    // hook in one of EXPLAINER_SHORT_STYLES.
    const [explainerFormat, setExplainerFormat] = useState<ExplainerFormat>('video');
    const [shortStyle, setShortStyle] = useState<ExplainerShortStyle>('auto');
    // The user's saved characters (a series' recurring cast) and the ones
    // picked for this video.
    const [savedCharacters, setSavedCharacters] = useState<{
        id: string;
        name: string;
        color: CharacterColor;
    }[]>([]);
    const [castIds, setCastIds] = useState<string[]>([]);
    const selectedExplainerLook = EXPLAINER_LOOKS.find((l) => l.id === explainerLook) ?? EXPLAINER_LOOKS[0];
    // 'your-own-media' workflow state — the uploader returns
    // { fileId: { name, path, size, type, url } } objects from Supabase. We
    // snapshot to an array of { id, url, type } when POSTing.
    const [uploadedUserMedia, setUploadedUserMedia] = useState<Record<string, {
        name: string;
        path: string;
        size?: number;
        type?: string;
        url?: string;
        durationSeconds?: number;
    }>>({});
    // True while MultiFileUpload has files in flight — blocks Generate so a
    // half-uploaded media set isn't silently dropped at submit time.
    const [mediaUploading, setMediaUploading] = useState(false);
    // Reference images uploaded from the button inside the prompt box —
    // photos of the real subject of the video (a property/development,
    // product, venue…). Passed to the backend as i2i references so generated
    // scenes show the real thing. Count tracks in-flight uploads so Generate
    // can block until they finish.
    const [referenceImages, setReferenceImages] = useState<ReferenceImage[]>([]);
    const [referenceUploadingCount, setReferenceUploadingCount] = useState(0);
    const referenceFileInputRef = useRef<HTMLInputElement | null>(null);
    const [convertImagesToVideo, setConvertImagesToVideo] = useState(false);
    const [useOnlyProvidedMedia, setUseOnlyProvidedMedia] = useState(true);
    const [smartCrop, setSmartCrop] = useState(true);
    const [enhancePhotos, setEnhancePhotos] = useState(false);
    const promptRef = useRef<HTMLTextAreaElement | null>(null);
    // Remembers the user's last real voice so toggling "No voiceover" off
    // restores it instead of stranding them on the muted state.
    const priorVoiceIdRef = useRef<string>(DEFAULT_VOICE.voice_id);
    // Until the user picks a voice, a video in another language gets a
    // narrator who speaks it (the default American voice reads Japanese with
    // an accent); back to English or Auto, the default returns.
    const voicePickedRef = useRef(false);
    const autoVoiceIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (voicePickedRef.current || voiceId === 'none')
            return;
        const label = language !== AUTO_VIDEO_LANGUAGE && language !== 'en'
            ? videoLanguageLabel(language)
            : undefined;
        const native = label ? nativeNarrator(modalVoices, label) : undefined;
        const next = native?.voice_id ?? DEFAULT_VOICE.voice_id;
        if (next !== voiceId &&
            (voiceId === DEFAULT_VOICE.voice_id || voiceId === autoVoiceIdRef.current)) {
            setVoiceId(next);
        }
        autoVoiceIdRef.current = native?.voice_id ?? null;
        // Follows the language (and the voice list once loaded), not the voice.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language, modalVoices]);
    // The two workflows where a recurring character is meaningful: image-story
    // (character image conditions every scene) and video-story (character
    // appears on camera + voices it).
    const supportsCharacter = workflowType === 'image-story' || workflowType === 'video-story';
    // Explainer: animated boards drawn from the narration. A narrator is
    // required, there's no style or image model to pick, 1:1 isn't offered,
    // and free plans get a short try.
    const isExplainer = workflowType === 'explainer';
    const isShort = isExplainer && explainerFormat === 'short';
    const maxDuration = isShort
        ? isFreeTier
            ? Math.min(EXPLAINER_DURATION.free, EXPLAINER_SHORT_DURATION.max)
            : EXPLAINER_SHORT_DURATION.max
        : isExplainer
            ? isFreeTier
                ? EXPLAINER_DURATION.free
                : EXPLAINER_DURATION.max
            : DEFAULT_MAX_DURATION;
    const minDuration = isShort ? EXPLAINER_SHORT_DURATION.min : 10;
    // The length the form shows and sends, inside the current range.
    const shownDuration = Math.min(Math.max(duration, minDuration), maxDuration);
    const offerAutoDuration = !(isExplainer && (isFreeTier || isShort));
    const explainerEstimate = isExplainer && !isFreeTier
        ? explainerCredits(explainerQuoteSeconds(shownDuration, autoDuration))
        : null;
    // useCharacter is derived: a character is in use when the user has
    // explicitly clicked at least one of their custom actor cards. Click
    // a card again to remove it from the selection.
    const useCharacter = actorIds.length > 0;
    const noVoice = voiceId === 'none';
    const noMusic = musicSource === 'none';
    // Reference images apply to the AI-generated visual workflows only:
    // 'your-own-media' already has its own uploader (the uploads ARE the
    // visuals), 'stock-videos' uses real footage and explainers draw boards.
    const supportsReferenceImages = workflowType !== 'your-own-media' &&
        workflowType !== 'stock-videos' &&
        workflowType !== 'explainer';
    const referenceUploading = referenceUploadingCount > 0;
    // Character is supported for both image-story and video-story. For
    // video-story it requires a voice (the actor speaks on camera) — when
    // noVoice is on we fold the flag back off without erasing the user's
    // choice so toggling a real voice brings the character back. For
    // image-story noVoice is allowed (silent image-story with character
    // continuity is a valid mode).
    const isCharacterFlow = useCharacter &&
        supportsCharacter &&
        (workflowType === 'image-story' || !noVoice);
    // What we actually send to the API. video-story + character routes via
    // the dedicated talking-video flow; image-story + character keeps the
    // image-story workflow and the backend uses the actorId as the recurring
    // character's i2i reference.
    const submittedWorkflowType: WorkflowType = isCharacterFlow && workflowType === 'video-story'
        ? 'talking-video'
        : workflowType;
    const mediaType = WORKFLOW_TO_MEDIA_TYPE[submittedWorkflowType];
    // Fetch custom actors as soon as the workflow can use a character — that
    // way the inline picker shows their cards immediately, instead of only
    // after they've already opted in. Auth flows through cookies, so we
    // don't need to wait on `useUser()`; refiring on `user` is still useful
    // so a user signing in mid-session sees their actors.
    useEffect(() => {
        if (typeof window !== 'undefined') {
            (window as any).__cvFx = ((window as any).__cvFx || 0) + 1;
        }
        if (!supportsCharacter)
            return;
        let cancelled = false;
        fetch('/api/custom-actors')
            .then((r) => r.json())
            .then((data) => {
            if (typeof window !== 'undefined') {
                (window as any).__cvData = data;
            }
            if (cancelled)
                return;
            setCustomActors(data?.actors || []);
        })
            .catch((error) => {
            console.warn('Could not load custom actors:', error);
        });
        return () => {
            cancelled = true;
        };
    }, [supportsCharacter, user]);
    // Fetch built-in preset voices (served via API to keep the ~25k-line VOICES
    // constant out of the client bundle) plus the user's cloned voices, so the
    // voice modal and create-actor flow can offer them.
    const ensureModalVoicesLoaded = useCallback(async () => {
        try {
            const supabase = createSupabaseComponentClient();
            const [presetsRes, customRes] = await Promise.all([
                fetch(VOICE_LIST_URL).then((r) => r.json()),
                supabase
                    .from('audio_voices')
                    .select('*')
                    .order('created_at', { ascending: false }),
            ]);
            const userVoices = (customRes.data || []) as Voice[];
            const presets: Voice[] = presetsRes?.voices ?? [];
            setModalVoices([...userVoices, ...presets]);
        }
        catch (error) {
            console.warn('Could not load user voices:', error);
        }
        finally {
            setModalVoicesLoading(false);
        }
    }, []);
    // Load voices once on mount so the on-page voice card resolves correctly
    // even before the picker modal is opened.
    useEffect(() => {
        ensureModalVoicesLoaded();
    }, [ensureModalVoicesLoaded]);
    const handleOpenActorModal = useCallback(async () => {
        await ensureModalVoicesLoaded();
        setShowActorModal(true);
    }, [ensureModalVoicesLoaded]);
    const handleOpenVoiceModal = useCallback(async () => {
        // Lazy-load user-cloned voices so the modal grid shows them alongside
        // the built-in ones — same pattern tts.tsx uses.
        await ensureModalVoicesLoaded();
        setShowVoiceModal(true);
    }, [ensureModalVoicesLoaded]);
    const handleActorSelected = useCallback((actor: Actor) => {
        // Add to selection (skipping duplicates) and treat this as the
        // primary actor when it's the first one. Auto-syncs voice to the
        // primary actor's default — but only when no other actors are
        // selected yet, so multi-actor leaves the voice picker manual.
        setActorIds((prev) => prev.includes(actor.id) ? prev : [...prev, actor.id]);
        if (actorIds.length === 0 && actor.voice_id) {
            setVoiceId(actor.voice_id);
        }
        // Custom actor was just created — fold it into our list so the
        // preview and summary update without a page reload.
        const isCustom = (actor as CustomActor).is_custom === true;
        if (isCustom) {
            setCustomActors((prev) => prev.some((a) => a.id === actor.id)
                ? prev
                : [actor as CustomActor, ...prev]);
        }
        setShowActorModal(false);
    }, [actorIds.length]);
    // All actors currently selected, in the order the user picked them.
    // First entry is the "primary" — it drives talking-head workflows and
    // (when alone) controls voice auto-sync.
    const selectedActors = useMemo(() => actorIds
        .map((id) => customActors.find((a) => a.id === id))
        .filter((a): a is CustomActor => !!a), [customActors, actorIds]);
    const selectedActor = selectedActors[0];
    // Generation state
    const [isGenerating, setIsGenerating] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    // Auth modal
    const [showAuth, setShowAuth] = useState(false);
    // Voice preview audio
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
    // Music preview audio. Kept separate from the voice player so playing a
    // track doesn't stop a voice preview the user already started (and vice
    // versa) — a tiny but real ergonomic win.
    const musicAudioRef = useRef<HTMLAudioElement | null>(null);
    const [playingTrackId, setPlayingTrackId] = useState<string | null>(null);
    const selectedLibraryTrack = useMemo<LibraryTrack>(() => MUSIC_LIBRARY.find((t) => t.id === libraryTrackId) || MUSIC_LIBRARY[0], [libraryTrackId]);
    const visibleLibraryTracks = useMemo<LibraryTrack[]>(() => libraryCategory === 'all'
        ? MUSIC_LIBRARY
        : MUSIC_LIBRARY.filter((t) => t.category === libraryCategory), [libraryCategory]);
    // Short, summary-card-friendly label for the AI tab. We show the typed
    // prompt truncated, or the same "AI music" placeholder the textarea has,
    // so the right rail stays compact and predictable.
    const aiMusicLabel = musicPrompt.trim()
        ? musicPrompt.trim().slice(0, 40)
        : 'AI music';
    const musicSummaryLabel = musicSource === 'auto'
        ? 'Auto music'
        : musicSource === 'library'
            ? selectedLibraryTrack.title
            : aiMusicLabel;
    const selectedVoice = useMemo(() => 
    // modalVoices already contains every built-in voice plus any cloned
    // user voices, so a custom voice picked in the modal still resolves.
    modalVoices.find((v) => v.voice_id === voiceId) || DEFAULT_VOICE, [voiceId, modalVoices]);
    const selectedStyle = useMemo(() => VIDEO_GENERATION_PRESETS.find((p) => p.id === preset), [preset]);
    const selectedWorkflow = useMemo(() => WORKFLOW_TYPES.find((w) => w.id === workflowType), [workflowType]);
    // Use-case landing preset, resolved from `?template=<slug>`. When matched it
    // pre-selects a workflow and swaps the page title / description / SEO so the
    // same form doubles as a focused landing page (e.g.
    // /create-video?template=real-estate-marketing-video).
    const agentPreset = getVideoAgentPreset(router.query.template);
    // Apply the preset's workflow once, after the router has hydrated the query.
    // Guarded by a ref so it seeds the default without fighting later manual
    // changes (and survives the re-render the setState triggers).
    const appliedPresetRef = useRef<string | null>(null);
    useEffect(() => {
        if (!router.isReady || !agentPreset)
            return;
        if (appliedPresetRef.current === agentPreset.slug)
            return;
        appliedPresetRef.current = agentPreset.slug;
        setWorkflowType(agentPreset.workflowType);
    }, [router.isReady, agentPreset]);
    // Picking a workflow. The Explainer brings its own defaults: a narrator
    // (it can't be silent), no burnt-in captions (the boards carry the text),
    // 16:9 or 9:16 and a supported language; leaving it restores captions and
    // the usual length range.
    const selectWorkflow = useCallback((id: WorkflowType) => {
        if (id === workflowType)
            return;
        if (id === 'explainer') {
            setCaptionsEnabled(false);
            setAspectRatio((ratio) => (ratio === '1:1' ? '16:9' : ratio));
            if (voiceId === 'none')
                setVoiceId(priorVoiceIdRef.current);
            setLanguage((code) => code === AUTO_VIDEO_LANGUAGE || isExplainerLanguage(code)
                ? code
                : AUTO_VIDEO_LANGUAGE);
        }
        else if (workflowType === 'explainer') {
            setCaptionsEnabled(true);
            setExplainerFormat('video');
            setDuration((d) => Math.min(d, DEFAULT_MAX_DURATION));
        }
        setWorkflowType(id);
    }, [workflowType, voiceId]);
    // A Short is vertical and 20-60 s; back to a video, the usual length.
    const chooseExplainerFormat = useCallback((format: ExplainerFormat) => {
        if (format === explainerFormat)
            return;
        setExplainerFormat(format);
        if (format === 'short') {
            setAspectRatio('9:16');
            setAutoDuration(false);
            setDuration(isFreeTier
                ? EXPLAINER_DURATION.free
                : EXPLAINER_SHORT_DURATION.default);
        }
        else {
            setDuration(isFreeTier ? EXPLAINER_DURATION.free : EXPLAINER_DURATION.default);
        }
    }, [explainerFormat, isFreeTier]);
    // Saved characters, once the Explainer is picked (none until any exist).
    useEffect(() => {
        if (!isExplainer || !user)
            return;
        let cancelled = false;
        fetch('/api/explainer/characters')
            .then((r) => (r.ok ? r.json() : { characters: [] }))
            .then((data) => {
            if (!cancelled)
                setSavedCharacters(data?.characters ?? []);
        })
            .catch(() => { });
        return () => {
            cancelled = true;
        };
    }, [isExplainer, user]);
    // A free Explainer is a short fixed-length try (also once the plan loads).
    useEffect(() => {
        if (!isExplainer || !isFreeTier)
            return;
        setAutoDuration(false);
        setDuration((d) => Math.min(d, EXPLAINER_DURATION.free));
    }, [isExplainer, isFreeTier]);
    const headerTitle = agentPreset?.title ?? 'Describe your idea. We’ll make the video.';
    const headerDescription = agentPreset?.description ??
        'Write a sentence. The agent writes the script, picks the voice and music, generates each scene, and hands off the project to the editor for fine-tuning.';
    const seoTitle = agentPreset?.seoTitle ?? agentPreset?.title ?? 'Video Agent';
    const seoDescription = agentPreset?.seoDescription ?? agentPreset?.description;
    // Picks a random example prompt and drops it into the textarea. Avoids
    // repeating the current value so a second click always feels fresh.
    const pickRandomPrompt = useCallback(() => {
        const pool = EXAMPLE_PROMPTS.filter((p) => p !== prompt);
        const next = pool[Math.floor(Math.random() * pool.length)] ?? EXAMPLE_PROMPTS[0];
        setPrompt(next);
        requestAnimationFrame(() => {
            promptRef.current?.focus();
            promptRef.current?.setSelectionRange(next.length, next.length);
        });
    }, [prompt]);
    const stylePreview = STYLE_PREVIEW[preset] || STYLE_PREVIEW.default;
    // Stop preview audio on unmount.
    useEffect(() => {
        return () => {
            audioRef.current?.pause();
            audioRef.current = null;
            musicAudioRef.current?.pause();
            musicAudioRef.current = null;
        };
    }, []);
    const handlePlayMusicTrack = useCallback((track: LibraryTrack) => {
        if (playingTrackId === track.id) {
            musicAudioRef.current?.pause();
            setPlayingTrackId(null);
            return;
        }
        musicAudioRef.current?.pause();
        const audio = new Audio(track.url);
        musicAudioRef.current = audio;
        audio.onended = () => setPlayingTrackId(null);
        audio.play().catch(() => setPlayingTrackId(null));
        setPlayingTrackId(track.id);
    }, [playingTrackId]);
    const handlePlayVoice = useCallback((id: string, url?: string) => {
        if (!url)
            return;
        if (playingVoiceId === id) {
            audioRef.current?.pause();
            setPlayingVoiceId(null);
            return;
        }
        audioRef.current?.pause();
        const audio = new Audio(url);
        audioRef.current = audio;
        audio.onended = () => setPlayingVoiceId(null);
        audio.play().catch(() => setPlayingVoiceId(null));
        setPlayingVoiceId(id);
    }, [playingVoiceId]);
    // Upload reference images picked via the button inside the prompt box.
    // Same storage path pattern as MultiFileUpload: compress → Supabase
    // 'uploads' bucket → public URL.
    const handleReferenceFilesSelected = useCallback(async (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0)
            return;
        const images = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
        // Reserve slots for in-flight uploads too — otherwise a second batch
        // picked before the first finishes could claim the full cap again,
        // uploading files that then get dropped on completion.
        const room = MAX_REFERENCE_IMAGES - referenceImages.length - referenceUploadingCount;
        if (images.length > room) {
            toast.error(`You can add up to ${MAX_REFERENCE_IMAGES} reference images`);
        }
        const batch = images.slice(0, Math.max(0, room));
        if (batch.length === 0)
            return;
        setReferenceUploadingCount((n) => n + batch.length);
        const supabase = createSupabaseComponentClient();
        await Promise.all(batch.map(async (rawFile) => {
            try {
                const file = await compressImage(rawFile, {
                    force: true,
                    format: 'jpeg',
                });
                const id = uuidv4();
                const fileExt = file.name.split('.').pop() || 'jpg';
                const filePath = `create-video/reference-images/${id}.${fileExt}`;
                const { error } = await supabase.storage
                    .from('uploads')
                    .upload(filePath, file);
                if (error)
                    throw error;
                const { data } = supabase.storage
                    .from('uploads')
                    .getPublicUrl(filePath);
                // Slot reservation above makes an over-cap completion
                // practically impossible; if it still happens, delete the
                // uploaded object instead of leaving an orphan in storage.
                let added = false;
                setReferenceImages((prev) => {
                    if (prev.length >= MAX_REFERENCE_IMAGES)
                        return prev;
                    added = true;
                    return [
                        ...prev,
                        {
                            id,
                            url: data.publicUrl,
                            path: filePath,
                            name: rawFile.name,
                        },
                    ];
                });
                if (!added) {
                    supabase.storage
                        .from('uploads')
                        .remove([filePath])
                        .catch(() => { });
                }
            }
            catch (error) {
                console.error('Reference image upload failed:', error);
                toast.error(`Failed to upload "${rawFile.name}"`);
            }
            finally {
                setReferenceUploadingCount((n) => n - 1);
            }
        }));
    }, [referenceImages.length, referenceUploadingCount]);
    const handleRemoveReferenceImage = useCallback((image: ReferenceImage) => {
        // Drop from state immediately; storage cleanup is best-effort (an
        // orphaned upload is harmless, a stuck thumbnail is not).
        setReferenceImages((prev) => prev.filter((r) => r.id !== image.id));
        const supabase = createSupabaseComponentClient();
        supabase.storage
            .from('uploads')
            .remove([image.path])
            .catch(() => { });
    }, []);
    const handleGenerate = useCallback(async () => {
        if (!user) {
            setShowAuth(true);
            return;
        }
        if (!prompt.trim()) {
            toast.error('Please enter a video prompt');
            return;
        }
        if (mediaUploading || referenceUploading) {
            toast.error('Please wait for media uploads to finish');
            return;
        }
        // Snapshot uploaded media into the wire shape the API expects. Drop any
        // entry that didn't finish uploading (no public URL yet).
        const userMediaPayload = Object.entries(uploadedUserMedia)
            .map(([id, file]) => {
            if (!file?.url)
                return null;
            const mime = (file.type || '').toLowerCase();
            const isVideo = mime.startsWith('video/');
            return {
                id,
                url: file.url,
                type: (isVideo ? 'video' : 'image') as 'video' | 'image',
                // Only attached for videos; the server uses this to constrain
                // scene durations and to drive the loop fallback in the editor.
                ...(isVideo && typeof file.durationSeconds === 'number'
                    ? { durationSeconds: file.durationSeconds }
                    : {}),
            };
        })
            .filter(<T,>(x: T | null): x is T => !!x);
        if (submittedWorkflowType === 'your-own-media' &&
            userMediaPayload.length === 0) {
            toast.error('Upload at least one image or video before generating a "Your own media" project');
            return;
        }
        setIsGenerating(true);
        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const response = await fetch('/api/generate-video-project', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt: prompt.trim(),
                    aspectRatio,
                    duration: autoDuration ? 'auto' : shownDuration,
                    workflowType: submittedWorkflowType,
                    explainerLook: submittedWorkflowType === 'explainer' ? explainerLook : undefined,
                    explainerSfx: submittedWorkflowType === 'explainer' ? explainerSfx : undefined,
                    explainerFormat: submittedWorkflowType === 'explainer' ? explainerFormat : undefined,
                    explainerShortStyle: submittedWorkflowType === 'explainer' && explainerFormat === 'short'
                        ? shortStyle
                        : undefined,
                    explainerCharacterIds: submittedWorkflowType === 'explainer' && castIds.length
                        ? castIds
                        : undefined,
                    mediaType,
                    preset,
                    presetId: preset,
                    videoModel,
                    imageModel,
                    voiceId,
                    actorIds: isCharacterFlow ? actorIds : undefined,
                    // Photos of the real subject (property/product/place) from the
                    // prompt-box uploader — the backend grounds the script and scene
                    // images in them. Not applicable to your-own-media/stock-videos.
                    referenceImages: supportsReferenceImages && referenceImages.length > 0
                        ? referenceImages.map(({ id, url }) => ({ id, url }))
                        : undefined,
                    // Music routing: 'auto' lets the server derive an instrumental
                    // brief from the prompt + workflow. 'library' hands an existing
                    // MP3 URL to the API (skips Suno generation). 'ai' sends the
                    // user's free-form prompt. 'none' skips music entirely.
                    musicSource,
                    musicUrl: musicSource === 'library' ? selectedLibraryTrack.url : undefined,
                    musicPrompt: musicSource === 'ai' ? musicPrompt.trim() || undefined : undefined,
                    // 'your-own-media' workflow inputs (ignored server-side for other
                    // workflows but cheap to always send).
                    userMedia: submittedWorkflowType === 'your-own-media'
                        ? userMediaPayload
                        : undefined,
                    convertImagesToVideo: submittedWorkflowType === 'your-own-media'
                        ? convertImagesToVideo
                        : undefined,
                    useOnlyProvidedMedia: submittedWorkflowType === 'your-own-media'
                        ? useOnlyProvidedMedia
                        : undefined,
                    smartCrop: submittedWorkflowType === 'your-own-media' ? smartCrop : undefined,
                    enhancePhotos: submittedWorkflowType === 'your-own-media'
                        ? enhancePhotos
                        : undefined,
                    captionPreset,
                    captionsEnabled,
                    language,
                }),
                signal: controller.signal,
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                // Paywalled workflow (free plan asking for AI video scenes) — show
                // the pricing modal instead of a dead-end toast.
                if (response.status === 403 && err?.code === 'upgrade_required') {
                    setShouldUpgrade(true);
                    setIsGenerating(false);
                    return;
                }
                throw new Error(err.error || 'Generation failed');
            }
            // The API streams a single 'project-created' event carrying the new
            // project id, then keeps generating in the background. As soon as we
            // have the id we send the user to the gallery, where the project shows
            // a live "generating" tile and turns into the finished video once the
            // backend marks it ready.
            const reader = response.body!.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';
                for (const line of lines) {
                    if (!line.startsWith('data: '))
                        continue;
                    let data: any;
                    try {
                        data = JSON.parse(line.slice(6));
                    }
                    catch {
                        continue; // skip malformed line
                    }
                    if (data.step === 'project-created' && data.projectId) {
                        controller.abort();
                        router.push('/ai-video-editor');
                        return;
                    }
                    if (data.step === 'error') {
                        throw new Error(data.message || 'Generation failed');
                    }
                }
            }
            // Stream closed without a project id — treat it as a failure.
            throw new Error('Could not start video generation. Please try again.');
        }
        catch (error: any) {
            if (error.name !== 'AbortError') {
                toast.error(error.message || 'Generation failed');
                setIsGenerating(false);
            }
        }
    }, [
        user,
        prompt,
        aspectRatio,
        shownDuration,
        autoDuration,
        submittedWorkflowType,
        explainerLook,
        explainerSfx,
        explainerFormat,
        shortStyle,
        castIds,
        mediaType,
        preset,
        videoModel,
        imageModel,
        voiceId,
        isCharacterFlow,
        actorIds,
        selectedLibraryTrack,
        musicPrompt,
        musicSource,
        router,
        uploadedUserMedia,
        mediaUploading,
        referenceUploading,
        referenceImages,
        supportsReferenceImages,
        convertImagesToVideo,
        useOnlyProvidedMedia,
        smartCrop,
        enhancePhotos,
        captionPreset,
        captionsEnabled,
        language,
    ]);
    // ── Render ─────────────────────────────────────────────────────
    // Logged-out visitors get the marketing landing (also what crawlers see —
    // `user` is undefined during SSR). Logged-in users briefly flash it while
    // useUser resolves, which beats making the anonymous majority wait.
    if (!user) {
        return (<VideoAgentLanding seoTitle={seoTitle} seoDescription={seoDescription}/>);
    }
    return (<NarrowDashboardLayout showMobileMenu={!!user} sidebarVisible={!!user}>
      <Seo templateTitle={seoTitle} description={seoDescription}/>

      <div className='h-full overflow-y-auto bg-[#fafafb] dark:bg-[#0b0b10]'>
        <div className='mx-auto max-w-6xl px-4 pb-32 pt-8 sm:px-6 sm:pt-10 lg:px-8 lg:pb-16 lg:pt-14'>
          {/* ── Header ─────────────────────────────────────────── */}
          <header className='cv-fade-in mb-10'>
            <div className='mb-3 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400'>
              <svg className='h-3.5 w-3.5 text-primary-400 dark:text-primary-300' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={1.75} strokeLinecap='round' aria-hidden>
                <line x1='12' y1='3' x2='12' y2='7'/>
                <line x1='12' y1='17' x2='12' y2='21'/>
                <line x1='3' y1='12' x2='7' y2='12'/>
                <line x1='17' y1='12' x2='21' y2='12'/>
                <line x1='5.6' y1='5.6' x2='8.5' y2='8.5'/>
                <line x1='15.5' y1='15.5' x2='18.4' y2='18.4'/>
                <line x1='5.6' y1='18.4' x2='8.5' y2='15.5'/>
                <line x1='15.5' y1='8.5' x2='18.4' y2='5.6'/>
              </svg>
              Video Agent
              <span className='rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-[0.06em] text-gray-500 dark:bg-white/10 dark:text-gray-400'>
                Beta
              </span>
            </div>
            <h1 className='text-[24px] font-semibold leading-[1.15] tracking-[-0.02em] text-gray-950 sm:text-[28px] dark:text-white'>
              {headerTitle}
            </h1>
            <p className='mt-3 max-w-2xl text-[14px] leading-[1.55] text-gray-600 dark:text-gray-400'>
              {headerDescription}
            </p>
          </header>

          {isGenerating ? (
        // ── Starting ───────────────────────────────────────
        <div className='cv-fade-in flex min-h-[420px] flex-col items-center justify-center text-center'>
              <div className='relative mb-6 flex h-14 w-14 items-center justify-center'>
                <span className='absolute h-14 w-14 animate-ping rounded-full bg-primary-400/30'/>
                <span className='h-7 w-7 rounded-full bg-primary-500/20'/>
                <span className='absolute h-2.5 w-2.5 rounded-full bg-primary-500'/>
              </div>
              <h2 className='text-2xl font-semibold tracking-tight text-gray-950 dark:text-white'>
                Starting your video
              </h2>
              <p className='mt-3 max-w-md text-sm leading-[1.55] text-gray-500 dark:text-gray-400'>
                Setting up your project — we&apos;ll take you to your videos,
                where it keeps generating. You can leave this page anytime; the
                video appears as soon as it&apos;s ready.
              </p>
            </div>) : (
        // ── Form ──────────────────────────────────────────
        <div className='cv-fade-in grid grid-cols-1 gap-x-12 gap-y-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,320px)]'>
              {/* Left column: form */}
              <div className='space-y-8'>
                {/* Prompt */}
                <Section number='01' title='Your idea' hint='A sentence works. A paragraph works better.'>
                  <div className='relative rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(15,15,25,0.04)] transition-shadow focus-within:border-primary-400 focus-within:shadow-[0_0_0_4px_rgba(99,102,241,0.12)] dark:border-white/10 dark:bg-white/[0.03] dark:focus-within:border-primary-400'>
                    <TextareaAutosize ref={promptRef} value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) &&
                    e.key === 'Enter' &&
                    prompt.trim()) {
                    e.preventDefault();
                    handleGenerate();
                }
            }} placeholder='A 30-second cinematic ad for a sustainable coffee brand, set in a sunlit roastery…' rows={5} maxRows={25} className='w-full resize-none rounded-2xl border-0 bg-transparent px-5 py-4 text-[15px] leading-relaxed text-gray-900 placeholder-gray-400 outline-none focus:ring-0 dark:text-white dark:placeholder-gray-500'/>
                    {/* Reference-image thumbnails — photos of the real
                subject (e.g. the condo project a realtor is
                launching). Uploaded via the button below; sent to
                the backend as i2i references so scenes show the
                real thing. */}
                    {supportsReferenceImages &&
                (referenceImages.length > 0 ||
                    referenceUploadingCount > 0) && (<div className='px-5 pb-1'>
                          <div className='flex flex-wrap items-center gap-2'>
                            {referenceImages.map((image) => (<div key={image.id} className='group relative h-14 w-14 overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-white/10 dark:bg-white/5'>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img src={image.url} alt={image.name} title={image.name} className='h-full w-full object-cover'/>
                                <button type='button' onClick={() => handleRemoveReferenceImage(image)} className='absolute right-0.5 top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity hover:bg-black/75 focus:opacity-100 group-hover:opacity-100' aria-label={`Remove ${image.name}`}>
                                  <svg className='h-2.5 w-2.5' viewBox='0 0 20 20' fill='currentColor'>
                                    <path d='M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z'/>
                                  </svg>
                                </button>
                              </div>))}
                            {Array.from({
                    length: referenceUploadingCount,
                }).map((_, i) => (<div key={`ref-uploading-${i}`} className='flex h-14 w-14 items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-white/15' aria-label='Uploading reference image'>
                                <svg className='h-4 w-4 animate-spin text-primary-500' viewBox='0 0 24 24' fill='none' aria-hidden>
                                  <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'/>
                                  <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z'/>
                                </svg>
                              </div>))}
                          </div>
                          <p className='mt-1.5 text-[11px] text-gray-500 dark:text-gray-400'>
                            Scenes will be grounded in these photos — great for
                            showing a real property, product, or place.
                          </p>
                        </div>)}
                    <div className='flex items-center justify-between gap-3 px-5 pb-3 pt-1'>
                      <span className='text-[12px] text-gray-400 dark:text-gray-500'>
                        Audience, tone, and what should happen — include them
                        all.
                      </span>
                      <div className='flex flex-shrink-0 items-center gap-1.5'>
                        {supportsReferenceImages && (<>
                            <input ref={referenceFileInputRef} type='file' accept='image/*' multiple className='hidden' onChange={(e) => {
                    handleReferenceFilesSelected(e.target.files);
                    // Allow re-picking the same file after remove.
                    e.target.value = '';
                }}/>
                            <button type='button' onClick={() => referenceFileInputRef.current?.click()} disabled={referenceImages.length +
                    referenceUploadingCount >=
                    MAX_REFERENCE_IMAGES} className='inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:border-primary-300 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300 dark:hover:border-primary-400/60 dark:hover:text-primary-300' title='Upload photos of the real subject — a property, product, or place — so the generated scenes show the real thing'>
                              <svg className='h-3 w-3' viewBox='0 0 20 20' fill='currentColor' aria-hidden>
                                <path fillRule='evenodd' d='M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z' clipRule='evenodd'/>
                              </svg>
                              {referenceImages.length > 0
                    ? `Reference images (${referenceImages.length})`
                    : 'Reference images'}
                              <span className='rounded-full bg-primary-500 px-1.5 py-px text-[9px] font-semibold uppercase leading-none text-white'>
                                New
                              </span>
                            </button>
                          </>)}
                        <button type='button' onClick={pickRandomPrompt} className='inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-medium text-gray-600 transition-colors hover:border-primary-300 hover:text-primary-600 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300 dark:hover:border-primary-400/60 dark:hover:text-primary-300' title='Drop a random example prompt into the box'>
                          <svg className='h-3 w-3' viewBox='0 0 20 20' fill='currentColor' aria-hidden>
                            <path d='M10 2a1 1 0 01.894.553l1.382 2.764 3.05.443a1 1 0 01.554 1.706l-2.207 2.151.521 3.04a1 1 0 01-1.451 1.054L10 12.347l-2.743 1.364a1 1 0 01-1.451-1.054l.521-3.04L4.12 7.466a1 1 0 01.554-1.706l3.05-.443L9.106 2.553A1 1 0 0110 2z'/>
                          </svg>
                          Surprise me
                        </button>
                      </div>
                    </div>
                  </div>
                </Section>

                <hr className='border-gray-200/70 dark:border-white/[0.06]'/>

                {/* Format */}
                <Section number='02' title='Format & length' hint='Where it will play, and how long.'>
                  <div className='grid grid-cols-1 gap-5 md:grid-cols-[auto_minmax(0,1fr)]'>
                    {isExplainer && (<ExplainerFormatPicker format={explainerFormat} onFormat={chooseExplainerFormat} shortStyle={shortStyle} onShortStyle={setShortStyle}/>)}
                    {/* Aspect ratio — compact horizontal pill row. Inline
                diagrams replaced with a thin border rectangle that
                scales with the actual ratio so the choice reads
                without labels. */}
                    <div>
                      <FieldLabel>Aspect ratio</FieldLabel>
                      {isShort ? (<p className='max-w-[15rem] rounded-lg border border-gray-200 px-3 py-1.5 text-[12px] text-gray-600 dark:border-white/10 dark:text-gray-400'>
                          Portrait 9:16, for YouTube Shorts, TikTok and Reels.
                        </p>) : (<div className='inline-flex flex-wrap gap-1.5'>
                          {[
                    { value: '9:16' as const, label: 'Portrait' },
                    { value: '16:9' as const, label: 'Landscape' },
                    { value: '1:1' as const, label: 'Square' },
                ]
                    // Explainer boards are drawn for 16:9 and 9:16.
                    .filter((opt) => !isExplainer || opt.value !== '1:1')
                    .map((opt) => {
                    const active = aspectRatio === opt.value;
                    return (<button key={opt.value} type='button' onClick={() => setAspectRatio(opt.value)} className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-[12px] font-medium transition-colors ${active
                            ? 'border-primary-500 bg-primary-50/60 text-primary-700 shadow-[0_0_0_2px_rgba(99,102,241,0.12)] dark:border-primary-400 dark:bg-primary-500/10 dark:text-primary-300'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:text-gray-900 dark:border-white/10 dark:bg-white/[0.03] dark:text-gray-300 dark:hover:border-white/20 dark:hover:text-gray-100'}`} aria-pressed={active} title={`${opt.label} · ${opt.value}`}>
                                  <span aria-hidden className={`flex-shrink-0 rounded-[2px] border ${active
                            ? 'border-primary-500 dark:border-primary-400'
                            : 'border-gray-400 dark:border-white/30'} ${opt.value === '9:16'
                            ? 'h-4 w-[9px]'
                            : opt.value === '1:1'
                                ? 'h-3.5 w-3.5'
                                : 'h-[9px] w-4'}`}/>
                                  {opt.label}
                                  <span className={`font-mono text-[10px] tabular-nums ${active
                            ? 'text-primary-500 dark:text-primary-300'
                            : 'text-gray-400 dark:text-gray-500'}`}>
                                    {opt.value}
                                  </span>
                                </button>);
                })}
                        </div>)}
                    </div>

                    {/* Duration — slider + auto pill in the same band. */}
                    <div>
                      <div className='mb-2 flex items-center justify-between'>
                        <div className='flex items-center gap-2'>
                          <FieldLabel>Target duration</FieldLabel>
                          {offerAutoDuration && (<button type='button' onClick={() => setAutoDuration((v) => !v)} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${autoDuration
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300 dark:hover:bg-white/15'}`} aria-pressed={autoDuration}>
                              Auto
                            </button>)}
                        </div>
                        <span className='font-mono text-[13px] font-semibold tabular-nums text-gray-950 dark:text-white'>
                          {autoDuration
                ? 'Auto'
                : isExplainer
                    ? formatExplainerDuration(shownDuration)
                    : `${shownDuration}s`}
                        </span>
                      </div>
                      {autoDuration ? (<p className='rounded-lg border border-dashed border-gray-200 px-3 py-2.5 text-[12px] text-gray-600 dark:border-white/15 dark:text-gray-400'>
                          {isExplainer
                    ? `The writer picks the length the topic needs, between ${EXPLAINER_DURATION.autoMin} and ${EXPLAINER_DURATION.autoMax} seconds.`
                    : 'The director picks the best length — typically 20–60s depending on whether it’s a listicle, explainer, or story.'}
                        </p>) : (<div className='px-1'>
                          <input type='range' min={minDuration} max={maxDuration} step={5} value={shownDuration} onChange={(e) => setDuration(parseInt(e.target.value))} className='cv-slider w-full' style={{
                    ['--cv-fill' as any]: `${((shownDuration - minDuration) /
                        (maxDuration - minDuration)) *
                        100}%`,
                } as React.CSSProperties}/>
                          {/* Ticks sit where their value is on the slider. */}
                          <div className='relative mt-1.5 h-3.5 text-[10px] tabular-nums text-gray-400'>
                            {(isShort
                    ? maxDuration === EXPLAINER_SHORT_DURATION.max
                        ? [20, 30, 45, 60]
                        : [20, 25, 30]
                    : maxDuration === EXPLAINER_DURATION.free
                        ? [10, 20, 30]
                        : maxDuration === EXPLAINER_DURATION.max
                            ? [10, 120, 300, 600]
                            : [10, 60, 120, 200]).map((tick, i, ticks) => (<span key={tick} className='absolute top-0 whitespace-nowrap' style={{
                        left: `${((tick - minDuration) / (maxDuration - minDuration)) * 100}%`,
                        transform: i === 0
                            ? 'none'
                            : i === ticks.length - 1
                                ? 'translateX(-100%)'
                                : 'translateX(-50%)',
                    }}>
                                {isExplainer
                        ? formatExplainerDuration(tick)
                        : `${tick}s`}
                              </span>))}
                          </div>
                          {isExplainer && !isFreeTier && (<p className='mt-2 text-[11px] text-gray-500 dark:text-gray-400'>
                              About {explainerGenerationMinutes(shownDuration)}{' '}
                              min to make.
                            </p>)}
                          {isExplainer && isFreeTier && (<p className='mt-2 text-[11px] text-gray-500 dark:text-gray-400'>
                              Free plan: explainers up to{' '}
                              {EXPLAINER_DURATION.free} seconds. Upgrade for up
                              to {EXPLAINER_DURATION.max / 60} minutes.
                            </p>)}
                        </div>)}
                    </div>
                  </div>
                </Section>

                <hr className='border-gray-200/70 dark:border-white/[0.06]'/>

                {/* Visuals */}
                <Section number='03' title='Visuals' hint='How each scene will look.'>
                  <div className='space-y-5'>
                    {/* Workflow type — equal-height grid so descriptions can
                wrap without one tab pushing the others taller. */}
                    <div>
                      <FieldLabel>Media type</FieldLabel>
                      <div className={`grid grid-cols-2 gap-2 ${EXPLAINER_ENABLED
                ? 'sm:grid-cols-3 xl:grid-cols-5'
                : 'sm:grid-cols-4'}`}>
                        {WORKFLOW_TYPES.filter((workflow) => workflow.id !== 'motion-graphics' &&
                (workflow.id !== 'explainer' || EXPLAINER_ENABLED)).map((workflow) => {
                const active = workflowType === workflow.id;
                // AI video scenes are paid-only; the card stays
                // visible so free users can discover it, but
                // selecting it opens the pricing modal.
                const lockedForFree = isFreeTier && workflow.id === 'video-story';
                return (<button key={workflow.id} type='button' onClick={() => lockedForFree
                        ? setShouldUpgrade(true)
                        : selectWorkflow(workflow.id)} className={`group relative flex h-full flex-col overflow-hidden rounded-lg border bg-white text-left transition-all dark:bg-white/[0.03] ${active
                        ? 'border-primary-500 shadow-[0_0_0_2px_rgba(99,102,241,0.15)] dark:border-primary-400'
                        : 'border-gray-200 opacity-60 hover:border-gray-300 hover:opacity-100 dark:border-white/10 dark:hover:border-white/20'}`} aria-pressed={active}>
                              <div className='relative aspect-[4/3] w-full overflow-hidden bg-gray-100 dark:bg-white/5'>
                                <video src={workflow.previewVideo} poster={workflow.previewPoster} autoPlay loop muted playsInline preload='metadata' aria-hidden className='h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'/>
                                {active && (<div className='absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-white shadow-[0_1px_3px_rgba(15,15,25,0.18)]'>
                                    <svg className='h-3 w-3' viewBox='0 0 20 20' fill='currentColor'>
                                      <path fillRule='evenodd' d='M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 011.4-1.4l2.8 2.8 6.8-6.8a1 1 0 011.4 0z' clipRule='evenodd'/>
                                    </svg>
                                  </div>)}
                                {lockedForFree && (<div className='absolute right-1.5 top-1.5 flex items-center gap-1 rounded-full bg-amber-500/95 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-[0_1px_3px_rgba(15,15,25,0.25)]'>
                                    <svg className='h-2.5 w-2.5' viewBox='0 0 20 20' fill='currentColor'>
                                      <path fillRule='evenodd' d='M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z' clipRule='evenodd'/>
                                    </svg>
                                    Upgrade
                                  </div>)}
                              </div>
                              <div className={`flex flex-1 flex-col px-3 py-2.5 ${active
                        ? 'bg-primary-50/40 dark:bg-primary-500/[0.08]'
                        : ''}`}>
                                <div className={`flex items-center gap-2 text-[13px] font-medium ${active
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-gray-900 dark:text-gray-100'}`}>
                                  {workflow.id === 'video-story' ? (<svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                                      <path d='M2 5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm14.55 1.55l2.69-1.79A1 1 0 0120 5.6v8.8a1 1 0 01-.76.97l-2.69-1.79V6.55z'/>
                                    </svg>) : workflow.id === 'motion-graphics' ? (<svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                                      <path d='M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm2.75 4.5a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5zm0 3a.75.75 0 000 1.5h4.5a.75.75 0 000-1.5h-4.5zm0 3a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5z'/>
                                    </svg>) : workflow.id === 'stock-videos' ? (<svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                                      <path fillRule='evenodd' d='M2 4a1 1 0 011-1h14a1 1 0 011 1v1a1 1 0 01-1 1H3a1 1 0 01-1-1V4zm1 4h14v8a1 1 0 01-1 1H4a1 1 0 01-1-1V8zm3 2.5a.5.5 0 01.5-.5h7a.5.5 0 010 1h-7a.5.5 0 01-.5-.5zm0 3a.5.5 0 01.5-.5h4a.5.5 0 010 1h-4a.5.5 0 01-.5-.5z' clipRule='evenodd'/>
                                    </svg>) : workflow.id === 'talking-video' ? (<svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                                      <path d='M10 2a4 4 0 00-4 4v3a4 4 0 008 0V6a4 4 0 00-4-4zm-6 7a1 1 0 011 1 5 5 0 0010 0 1 1 0 112 0 7 7 0 01-6 6.93V18h2a1 1 0 110 2H7a1 1 0 110-2h2v-1.07A7 7 0 013 10a1 1 0 011-1z'/>
                                    </svg>) : workflow.id === 'your-own-media' ? (<svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                                      <path d='M3 4a1 1 0 011-1h3.586a1 1 0 01.707.293L9.414 4.5H16a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm5 6.5a2.5 2.5 0 115 0 2.5 2.5 0 01-5 0z'/>
                                    </svg>) : workflow.id === 'explainer' ? (
                    // A board on an easel.
                    <svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                                      <path fillRule='evenodd' d='M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm4 3.5a1.5 1.5 0 100 3 1.5 1.5 0 000-3zM10.5 8a.75.75 0 000 1.5h3a.75.75 0 000-1.5h-3zm-4 3a.75.75 0 000 1.5h7a.75.75 0 000-1.5h-7z' clipRule='evenodd'/>
                                    </svg>) : (<svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                                      <path fillRule='evenodd' d='M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z' clipRule='evenodd'/>
                                    </svg>)}
                                  {workflow.label}
                                </div>
                                <div className='mt-0.5 text-[11px] leading-tight text-gray-500 dark:text-gray-400'>
                                  {workflow.description}
                                </div>
                              </div>
                            </button>);
            })}
                      </div>

                      {supportsCharacter && (<CharacterPicker actors={customActors} selectedActorIds={actorIds} onToggle={(actor) => {
                    // Toggle this actor in/out of the selection. The
                    // first-picked actor's voice auto-syncs (matches
                    // the old single-actor flow); once 2+ are
                    // selected the voice picker stays manual so the
                    // user explicitly chooses the narrator.
                    const isSelected = actorIds.includes(actor.id);
                    if (isSelected) {
                        setActorIds((prev) => prev.filter((id) => id !== actor.id));
                    }
                    else {
                        const wasEmpty = actorIds.length === 0;
                        setActorIds((prev) => [...prev, actor.id]);
                        if (wasEmpty && actor.voice_id) {
                            setVoiceId(actor.voice_id);
                        }
                    }
                }} onOpenCreate={handleOpenActorModal}/>)}

                      {workflowType === 'your-own-media' && (<div className='mt-3 space-y-3 rounded-lg border border-gray-200 bg-gray-50/50 p-3 dark:border-white/10 dark:bg-white/[0.02]'>
                          <div>
                            <FieldLabel>Upload your photos / videos</FieldLabel>
                            <MultiFileUpload onFilesChange={setUploadedUserMedia} accept='image/*,video/*' acceptDescription='Images and videos' maxFiles={20} maxSizeBytes={50 * 1024 * 1024} uploadPath='create-video/user-media' initialFiles={uploadedUserMedia} onUploadingChange={setMediaUploading} label=''/>
                            <div className='mt-1.5 text-[11px] text-gray-500 dark:text-gray-400'>
                              The director will write the script around what's
                              in these uploads. Tip: 5–8 photos works best for a
                              30s video.
                            </div>
                          </div>

                          <div className='space-y-2'>
                            <YourOwnMediaToggle checked={useOnlyProvidedMedia} onChange={setUseOnlyProvidedMedia} title='Use only provided media' description='If off, scenes that fail to match an upload fall back to stock or generated visuals.'/>
                            <YourOwnMediaToggle checked={enhancePhotos} onChange={setEnhancePhotos} title='Enhance photos' description='Brighten shadows, even out exposure, correct white balance with Nano Banana 2. Composition and furniture stay untouched. Costs ~1 image credit per photo.'/>
                            <YourOwnMediaToggle checked={smartCrop} onChange={setSmartCrop} title='Smart crop' description='Crop each photo to the most informative region when its aspect doesn’t match the project.'/>
                            <YourOwnMediaToggle checked={isFreeTier ? false : convertImagesToVideo} onChange={(value: boolean) => {
                    if (isFreeTier) {
                        setShouldUpgrade(true);
                        return;
                    }
                    setConvertImagesToVideo(value);
                }} title='Turn images into videos' description={isFreeTier
                    ? 'Generate a short animated clip per photo (i2v) instead of using stills. Available on paid plans.'
                    : 'Generate a short animated clip per photo (i2v) instead of using stills. Costs extra credits.'}/>
                          </div>
                        </div>)}
                    </div>

                    {/* Style — popular eight by default; the rest live behind
                an inline "Show all" expander. The active style is
                always shown even when collapsed so the user never
                loses sight of their selection. Hidden for
                'your-own-media' (user's photos are the visual style)
                and 'stock-videos' (real stock footage — no AI
                preset applies). */}
                    {workflowType !== 'your-own-media' &&
                workflowType !== 'stock-videos' &&
                !isExplainer && (<div>
                          <div className='mb-2 flex items-baseline gap-3'>
                            <FieldLabel>Style</FieldLabel>
                            <button type='button' onClick={() => setShowAllStyles((v) => !v)} className='text-[12px] font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300'>
                              {showAllStyles
                    ? 'Show fewer'
                    : `Show all ${VIDEO_GENERATION_PRESETS.length}`}
                            </button>
                          </div>
                          <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'>
                            {VIDEO_GENERATION_PRESETS.filter((p) => showAllStyles ||
                    POPULAR_STYLE_IDS.has(p.id) ||
                    preset === p.id).map((p) => {
                    const sp = STYLE_PREVIEW[p.id] || STYLE_PREVIEW.default;
                    const active = preset === p.id;
                    return (<button key={p.id} type='button' onClick={() => setPreset(p.id)} className={`group relative overflow-hidden rounded-lg border text-left transition-colors ${active
                            ? 'border-primary-500 shadow-[0_0_0_2px_rgba(99,102,241,0.18)]'
                            : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20'}`} aria-pressed={active} title={p.description}>
                                  <div className='relative aspect-[5/4] w-full overflow-hidden' style={{ background: sp.bg }}>
                                    {p.thumbnail && (<Image src={p.thumbnail} alt='' fill sizes='(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw' className='object-cover transition-transform duration-300 group-hover:scale-[1.02]'/>)}
                                    {active && (<div className='absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-white shadow-sm'>
                                        <svg className='h-3 w-3' viewBox='0 0 20 20' fill='currentColor'>
                                          <path fillRule='evenodd' d='M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 011.4-1.4l2.8 2.8 6.8-6.8a1 1 0 011.4 0z' clipRule='evenodd'/>
                                        </svg>
                                      </div>)}
                                  </div>
                                  <div className={`px-2.5 py-1.5 text-[12px] font-medium leading-tight ${active
                            ? 'bg-primary-50/60 text-primary-700 dark:bg-primary-500/10 dark:text-primary-300'
                            : 'bg-white text-gray-800 dark:bg-white/[0.03] dark:text-gray-200'}`}>
                                    {p.label}
                                  </div>
                                </button>);
                })}
                          </div>
                        </div>)}

                    {isExplainer && (<div>
                        <FieldLabel>Look</FieldLabel>
                        <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'>
                          {EXPLAINER_LOOKS.map((look) => {
                    const active = explainerLook === look.id;
                    return (<button key={look.id} type='button' onClick={() => setExplainerLook(look.id)} onMouseEnter={() => setHoveredLook(look.id)} onMouseLeave={() => setHoveredLook((h) => h === look.id ? null : h)} className={`group relative flex h-full flex-col overflow-hidden rounded-lg border text-left transition-colors ${active
                            ? 'border-primary-500 shadow-[0_0_0_2px_rgba(99,102,241,0.18)]'
                            : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20'}`} aria-pressed={active}>
                                <div className='relative aspect-video w-full overflow-hidden bg-gray-100 dark:bg-white/5'>
                                  <Image src={look.image} alt='' fill unoptimized sizes='(max-width: 640px) 50vw, 20vw' className='object-cover transition-transform duration-300 group-hover:scale-[1.02]'/>
                                  {hoveredLook === look.id && (<video src={look.clip} autoPlay loop muted playsInline aria-hidden className='absolute inset-0 h-full w-full object-cover'/>)}
                                  {active && (<div className='absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-white shadow-sm'>
                                      <svg className='h-3 w-3' viewBox='0 0 20 20' fill='currentColor'>
                                        <path fillRule='evenodd' d='M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 011.4-1.4l2.8 2.8 6.8-6.8a1 1 0 011.4 0z' clipRule='evenodd'/>
                                      </svg>
                                    </div>)}
                                </div>
                                <div className={`flex-1 px-2.5 py-1.5 leading-tight ${active
                            ? 'bg-primary-50/60 dark:bg-primary-500/10'
                            : 'bg-white dark:bg-white/[0.03]'}`}>
                                  <div className={`text-[12px] font-medium ${active
                            ? 'text-primary-700 dark:text-primary-300'
                            : 'text-gray-800 dark:text-gray-200'}`}>
                                    {look.label}
                                  </div>
                                  <div className='mt-0.5 text-[11px] text-gray-500 dark:text-gray-400'>
                                    {look.description}
                                  </div>
                                </div>
                              </button>);
                })}
                        </div>
                        {savedCharacters.length > 0 && (<div className='mt-4'>
                            <FieldLabel>Your characters</FieldLabel>
                            <div className='flex flex-wrap gap-1.5'>
                              {savedCharacters.map((c) => {
                        const on = castIds.includes(c.id);
                        return (<button key={c.id} type='button' aria-pressed={on} onClick={() => setCastIds((ids) => on
                                ? ids.filter((id) => id !== c.id)
                                : ids.length < 4
                                    ? [...ids, c.id]
                                    : ids)} className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ${on
                                ? 'border-primary-500 bg-primary-50/60 text-primary-700 dark:border-primary-400 dark:bg-primary-500/10 dark:text-primary-300'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-white/10 dark:bg-white/[0.03] dark:text-gray-300 dark:hover:border-white/20'}`}>
                                    <span aria-hidden className='h-3 w-3 rounded-full border border-black/20' style={{
                                background: THEMES.classic.characterFills[c.color],
                            }}/>
                                    {c.name}
                                  </button>);
                    })}
                            </div>
                            <p className='mt-1.5 text-[11px] text-gray-500 dark:text-gray-400'>
                              Pick up to 4 to make them this video's characters,
                              so a series keeps its cast.
                            </p>
                          </div>)}
                        {explainerLookHasSfx(explainerLook) && (<div className='mt-3 flex items-center justify-between gap-4'>
                            <div>
                              <div className='text-[13px] font-medium text-gray-900 dark:text-gray-100'>
                                Sound effects
                              </div>
                              <p className='text-[11px] text-gray-500 dark:text-gray-400'>
                                Soft pops and swishes as things appear, under
                                the voiceover and music.
                              </p>
                            </div>
                            <button type='button' role='switch' aria-checked={explainerSfx} aria-label='Sound effects' onClick={() => setExplainerSfx((v) => !v)} className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${explainerSfx
                        ? 'bg-primary-500'
                        : 'bg-gray-300 dark:bg-white/20'}`}>
                              <span aria-hidden='true' className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${explainerSfx
                        ? 'translate-x-5'
                        : 'translate-x-0'}`}/>
                            </button>
                          </div>)}
                      </div>)}

                    {/* Video model — for workflows that produce AI video
                clips: video-story, talking-video, and your-own-media
                when "Turn images into videos" (i2v) is on. Stock
                videos use real footage, so the picker doesn't apply. */}
                    {(submittedWorkflowType === 'video-story' ||
                submittedWorkflowType === 'talking-video' ||
                (submittedWorkflowType === 'your-own-media' &&
                    convertImagesToVideo)) && (<div>
                        <FieldLabel>Video model</FieldLabel>
                        <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4'>
                          {VIDEO_MODELS
                    // Temporarily hide Veo 3.1 models from the picker.
                    // Remove this filter to re-enable them.
                    .filter((m) => m.id !== 'veo-3.1' && m.id !== 'veo-3.1-fast')
                    .map((m) => {
                    const active = videoModel === m.id;
                    const Icon = m.icon;
                    return (<button key={m.id} type='button' onClick={() => setVideoModel(m.id)} className={`group relative flex flex-col rounded-xl border bg-white px-3 py-2.5 text-left transition-all dark:bg-white/[0.03] ${active
                            ? 'border-primary-500 shadow-[0_0_0_3px_rgba(99,102,241,0.15)]'
                            : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20'}`} aria-pressed={active} title={m.description}>
                                  <div className='flex items-baseline justify-between gap-2'>
                                    <span className={`flex items-center gap-1.5 text-sm font-medium ${active
                            ? 'text-primary-700 dark:text-primary-400'
                            : 'text-gray-900 dark:text-gray-100'}`}>
                                      {Icon && (<Icon className='h-3.5 w-3.5 shrink-0'/>)}
                                      {m.label}
                                    </span>
                                    <span className={`whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${active
                            ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300'}`}>
                                      {m.creditsPerSecond} cr/s
                                    </span>
                                  </div>
                                  <span className='mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400'>
                                    {m.description}
                                  </span>
                                </button>);
                })}
                        </div>
                        <p className='mt-2 text-[11px] text-gray-500 dark:text-gray-400'>
                          Credits/sec is per generated clip — a 6-second clip at{' '}
                          {VIDEO_MODELS.find((m) => m.id === videoModel)
                    ?.creditsPerSecond ?? 0}{' '}
                          cr/s costs{' '}
                          {(VIDEO_MODELS.find((m) => m.id === videoModel)
                    ?.creditsPerSecond ?? 0) * 6}{' '}
                          credits.
                        </p>
                      </div>)}
                  </div>
                </Section>

                <hr className='border-gray-200/70 dark:border-white/[0.06]'/>

                {/* Audio */}
                <Section number='04' title='Audio' hint='Voice and music.'>
                  <div className='space-y-5'>
                    {/* Voice picker — always available. When a character is
                selected we sync the voiceId to the actor's default
                voice but the user can still pick a different one. */}
                    <div>
                      <FieldLabel>Voice</FieldLabel>
                      {isCharacterFlow && selectedActor && (<p className='mb-2 text-[11px] text-gray-500 dark:text-gray-400'>
                          {selectedActors.length > 1
                    ? `Multiple characters selected — pick the narrator voice yourself.`
                    : `Defaulting to ${selectedActor.name}'s voice. Pick a different one below to override for this video.`}
                        </p>)}
                      {isExplainer && (<p className='mb-2 text-[11px] text-gray-500 dark:text-gray-400'>
                          The narrator tells the story; the boards draw along.
                        </p>)}
                      <div className={`grid grid-cols-1 gap-1.5 ${isExplainer ? '' : 'sm:grid-cols-[auto_minmax(0,1fr)]'}`}>
                        {/* Explainers always have a narrator. */}
                        {!isExplainer && (<button type='button' onClick={() => {
                    // Save the current voice so toggling back
                    // restores it instead of dropping the user
                    // on a default they didn't pick.
                    if (!noVoice) {
                        priorVoiceIdRef.current = voiceId;
                    }
                    setVoiceId('none');
                }} aria-pressed={noVoice} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${noVoice
                    ? 'border-primary-500 bg-primary-50/60 dark:border-primary-400 dark:bg-primary-500/10'
                    : 'border-dashed border-gray-300 hover:border-gray-400 dark:border-white/15 dark:hover:border-white/30'}`}>
                            <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-400 dark:bg-white/10 dark:text-gray-500'>
                              <svg className='h-3.5 w-3.5' viewBox='0 0 20 20' fill='currentColor'>
                                <path fillRule='evenodd' d='M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.787L4.65 13.5H2a1 1 0 01-1-1V7.5a1 1 0 011-1h2.65l3.733-3.287a1 1 0 011-.137zm5.97 3.464a.75.75 0 011.06 1.06L15.06 9l1.353 1.4a.75.75 0 11-1.06 1.06L14 10.06l-1.354 1.4a.75.75 0 11-1.06-1.06L12.94 9l-1.354-1.4a.75.75 0 011.06-1.06L14 7.94l1.354-1.4z' clipRule='evenodd'/>
                              </svg>
                            </span>
                            <span className={`truncate text-[12px] font-medium ${noVoice
                    ? 'text-primary-700 dark:text-primary-300'
                    : 'text-gray-700 dark:text-gray-300'}`}>
                              No voiceover
                            </span>
                          </button>)}
                        <div className={`group relative flex items-center gap-3 rounded-lg border bg-white px-3 py-2 transition-colors dark:bg-white/[0.03] ${noVoice
                ? 'cursor-pointer border-gray-200 opacity-60 hover:border-primary-400 hover:opacity-100 dark:border-white/10 dark:hover:border-primary-400'
                : 'border-primary-500 bg-primary-50/40 dark:border-primary-400 dark:bg-primary-500/[0.08]'}`} onClick={noVoice
                ? () => setVoiceId(priorVoiceIdRef.current)
                : undefined} role={noVoice ? 'button' : undefined} tabIndex={noVoice ? 0 : undefined} onKeyDown={noVoice
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setVoiceId(priorVoiceIdRef.current);
                    }
                }
                : undefined} aria-label={noVoice
                ? `Use ${selectedVoice?.name || 'a voice'} instead`
                : undefined}>
                          <button type='button' onClick={(e) => {
                if (noVoice) {
                    // Let the wrapper's restore handler run; the
                    // inner button shouldn't open the modal while
                    // the card is in the muted state.
                    e.stopPropagation();
                    setVoiceId(priorVoiceIdRef.current);
                    return;
                }
                handleOpenVoiceModal();
            }} className='flex flex-1 items-center gap-2.5 text-left' aria-label={noVoice ? 'Use a voice' : 'Change voice'} tabIndex={noVoice ? -1 : 0}>
                            <div className='relative h-8 w-8 flex-shrink-0 overflow-hidden rounded-full bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800'>
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img src={getVoiceImageUrl(selectedVoice?.image)} alt={selectedVoice?.name || 'Voice'} className='h-full w-full object-cover' onError={(e) => {
                (e.target as HTMLImageElement).style.display =
                    'none';
            }}/>
                            </div>
                            <div className='min-w-0 flex-1'>
                              <div className='truncate text-[13px] font-medium text-gray-900 dark:text-gray-100'>
                                {selectedVoice?.name || 'Choose a voice'}
                              </div>
                              <div className='truncate text-[11px] text-gray-500 dark:text-gray-400'>
                                {noVoice
                ? 'Tap to use this voice'
                : selectedVoice?.accent ||
                    selectedVoice?.category ||
                    'Tap to change'}
                              </div>
                            </div>
                            <span className='text-[11px] font-medium text-primary-600 dark:text-primary-400'>
                              {noVoice ? 'Use' : 'Change'}
                            </span>
                          </button>
                          {!noVoice && selectedVoice?.preview_url && (<button type='button' onClick={(e) => {
                    e.stopPropagation();
                    handlePlayVoice(selectedVoice.voice_id, selectedVoice.preview_url);
                }} className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-primary-500 hover:text-white dark:bg-white/10 dark:text-gray-300' aria-label={playingVoiceId === selectedVoice.voice_id
                    ? `Stop preview of ${selectedVoice.name}`
                    : `Play preview of ${selectedVoice.name}`}>
                              {playingVoiceId === selectedVoice.voice_id ? (<svg className='h-3 w-3' viewBox='0 0 20 20' fill='currentColor'>
                                  <path d='M5.25 3.5A1.5 1.5 0 016.75 5v10a1.5 1.5 0 01-3 0V5a1.5 1.5 0 011.5-1.5zm8 0A1.5 1.5 0 0114.75 5v10a1.5 1.5 0 01-3 0V5a1.5 1.5 0 011.5-1.5z'/>
                                </svg>) : (<svg className='ml-0.5 h-3 w-3' viewBox='0 0 20 20' fill='currentColor'>
                                  <path d='M6.3 4.5a1 1 0 011.5-.87l9 5.5a1 1 0 010 1.74l-9 5.5A1 1 0 016.3 15.5v-11z'/>
                                </svg>)}
                            </button>)}
                        </div>
                      </div>

                      <Modal showModal={showVoiceModal} onClose={() => setShowVoiceModal(false)} className={VOICE_PICKER_PANEL_CLASS}>
                        <VoicesModal voices={modalVoices} 
        // VoicesModal calls setVoice(name) + setVoiceID(id)
        // separately. We derive the displayed name from
        // modalVoices so we only need the id.
        setVoice={() => { }} setVoiceID={(id) => {
                voicePickedRef.current = true;
                setVoiceId(id);
            }} currentVoice={selectedVoice?.name || null} currentVoiceId={voiceId} closeModal={() => setShowVoiceModal(false)} isLoading={modalVoicesLoading}/>
                      </Modal>
                    </div>

                    {/* Music — three-mode picker. Pre-uploaded "Library"
                tracks (instant, no credits), "AI" composer (Suno-
                generated, takes a minute), and "None" (silent). The
                tab row is the *source*; the body below is what that
                source lets the user pick. */}
                    <div>
                      <FieldLabel>Background music</FieldLabel>
                      <div role='tablist' aria-label='Music source' className='mb-3 inline-flex rounded-lg bg-gray-100 p-0.5 dark:bg-white/5'>
                        {([
                { id: 'library', label: 'Library' },
                { id: 'auto', label: 'Auto' },
                { id: 'ai', label: 'AI Generate' },
                { id: 'none', label: 'No music' },
            ] as const)
                // AI-generated music (Suno) is billed per track, so
                // free projects use the zero-cost library instead.
                .filter((tab) => !isFreeTier ||
                (tab.id !== 'ai' && tab.id !== 'auto'))
                .map((tab) => {
                const active = musicSource === tab.id;
                return (<button key={tab.id} type='button' role='tab' aria-selected={active} onClick={() => {
                        if (active)
                            return;
                        // Pause any preview when leaving the library
                        // tab so audio doesn't keep playing in a
                        // collapsed panel.
                        if (tab.id !== 'library') {
                            musicAudioRef.current?.pause();
                            setPlayingTrackId(null);
                        }
                        setMusicSource(tab.id);
                    }} className={`rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors ${active
                        ? 'bg-white text-gray-950 shadow-sm dark:bg-white/15 dark:text-white'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'}`}>
                                {tab.label}
                              </button>);
            })}
                      </div>

                      {musicSource === 'auto' && (<div className='rounded-lg border border-dashed border-gray-200 bg-gray-50/50 p-3 dark:border-white/10 dark:bg-white/[0.02]'>
                          <p className='text-[12px] text-gray-600 dark:text-gray-400'>
                            We'll match instrumental music to your topic and
                            duration — genre, mood, tempo, and instruments are
                            picked automatically. No vocals.
                          </p>
                        </div>)}

                      {musicSource === 'library' && (<div>
                          {/* Category filter — chip row. Click to narrow the
                    list below; "All" resets. Tracks are organised
                    by category in the constants file so this just
                    filters the existing array. */}
                          <div role='tablist' aria-label='Music category' className='mb-2 flex flex-wrap gap-1.5'>
                            {LIBRARY_CATEGORIES.map((c) => {
                    const active = libraryCategory === c.id;
                    return (<button key={c.id} type='button' role='tab' aria-selected={active} onClick={() => setLibraryCategory(c.id)} className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${active
                            ? 'bg-primary-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-white/10 dark:text-gray-300 dark:hover:bg-white/15'}`}>
                                  {c.label}
                                </button>);
                })}
                          </div>
                          <div className='max-h-[280px] overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/30 p-1.5 dark:border-white/10 dark:bg-white/[0.02]'>
                            <div className='grid grid-cols-1 gap-1.5 sm:grid-cols-2'>
                              {visibleLibraryTracks.map((track) => {
                    const active = libraryTrackId === track.id;
                    const isPlaying = playingTrackId === track.id;
                    const mins = Math.floor(track.durationSeconds / 60);
                    const secs = Math.floor(track.durationSeconds % 60);
                    const durLabel = `${mins}:${secs
                        .toString()
                        .padStart(2, '0')}`;
                    return (<div key={track.id} className={`group flex items-center gap-2.5 rounded-lg border bg-white px-2.5 py-2 transition-colors dark:bg-white/[0.03] ${active
                            ? 'border-primary-500 bg-primary-50/40 dark:border-primary-400 dark:bg-primary-500/[0.08]'
                            : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20'}`}>
                                    <button type='button' onClick={(e) => {
                            e.stopPropagation();
                            handlePlayMusicTrack(track);
                        }} className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors ${isPlaying
                            ? 'bg-primary-500 text-white'
                            : active
                                ? 'bg-primary-500/15 text-primary-600 dark:text-primary-300'
                                : 'bg-gray-100 text-gray-600 hover:bg-primary-500 hover:text-white dark:bg-white/10 dark:text-gray-300'}`} aria-label={isPlaying
                            ? `Stop preview of ${track.title}`
                            : `Play preview of ${track.title}`}>
                                      {isPlaying ? (<svg className='h-3 w-3' viewBox='0 0 20 20' fill='currentColor'>
                                          <path d='M5.25 3.5A1.5 1.5 0 016.75 5v10a1.5 1.5 0 01-3 0V5a1.5 1.5 0 011.5-1.5zm8 0A1.5 1.5 0 0114.75 5v10a1.5 1.5 0 01-3 0V5a1.5 1.5 0 011.5-1.5z'/>
                                        </svg>) : (<svg className='ml-0.5 h-3 w-3' viewBox='0 0 20 20' fill='currentColor'>
                                          <path d='M6.3 4.5a1 1 0 011.5-.87l9 5.5a1 1 0 010 1.74l-9 5.5A1 1 0 016.3 15.5v-11z'/>
                                        </svg>)}
                                    </button>
                                    <button type='button' onClick={() => setLibraryTrackId(track.id)} aria-pressed={active} className='flex min-w-0 flex-1 items-center justify-between text-left'>
                                      <span className='min-w-0 flex-1'>
                                        <span className={`block truncate text-[12px] font-medium leading-tight ${active
                            ? 'text-primary-700 dark:text-primary-300'
                            : 'text-gray-900 dark:text-gray-100'}`}>
                                          {track.title}
                                        </span>
                                        <span className='block truncate text-[10px] text-gray-500 dark:text-gray-400'>
                                          {track.vibe} · {durLabel}
                                          {track.composer && (<>
                                              {' · '}
                                              <span className='text-gray-400 dark:text-gray-500'>
                                                by {track.composer}
                                              </span>
                                            </>)}
                                        </span>
                                      </span>
                                      {active && (<span className='ml-2 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-white'>
                                          <svg className='h-2.5 w-2.5' viewBox='0 0 20 20' fill='currentColor'>
                                            <path fillRule='evenodd' d='M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 011.42-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z' clipRule='evenodd'/>
                                          </svg>
                                        </span>)}
                                    </button>
                                  </div>);
                })}
                              {visibleLibraryTracks.length === 0 && (<p className='col-span-full px-2 py-4 text-center text-[11px] text-gray-500 dark:text-gray-400'>
                                  No tracks in this category yet.
                                </p>)}
                            </div>
                          </div>
                        </div>)}

                      {musicSource === 'ai' && (<div>
                          <textarea value={musicPrompt} onChange={(e) => setMusicPrompt(e.target.value)} placeholder='Describe the music — e.g. "warm lofi piano with rain", "punchy synthwave with a 4-on-the-floor kick", "soft acoustic guitar"' rows={3} className='block w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-[13px] leading-relaxed text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:bg-white/[0.03] dark:text-gray-100 dark:placeholder:text-gray-500'/>
                          <p className='mt-1.5 text-[11px] text-gray-500 dark:text-gray-400'>
                            Always instrumental — no vocals. Leave blank for a
                            generic uplifting track.
                          </p>
                        </div>)}

                      {musicSource === 'none' && (<div className='flex items-start gap-3 rounded-lg border border-dashed border-gray-300 bg-gray-50/50 px-3 py-3 dark:border-white/15 dark:bg-white/[0.02]'>
                          <span className='flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-400 dark:bg-white/10 dark:text-gray-500'>
                            <svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor'>
                              <path fillRule='evenodd' d='M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.787L4.65 13.5H2a1 1 0 01-1-1V7.5a1 1 0 011-1h2.65l3.733-3.287a1 1 0 011-.137zm5.97 3.464a.75.75 0 011.06 1.06L15.06 9l1.353 1.4a.75.75 0 11-1.06 1.06L14 10.06l-1.354 1.4a.75.75 0 11-1.06-1.06L12.94 9l-1.354-1.4a.75.75 0 011.06-1.06L14 7.94l1.354-1.4z' clipRule='evenodd'/>
                            </svg>
                          </span>
                          <div className='min-w-0 flex-1'>
                            <p className='text-[13px] font-medium text-gray-900 dark:text-gray-100'>
                              Silent video
                            </p>
                            <p className='mt-0.5 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400'>
                              {noVoice
                    ? 'Captions and visuals will carry the story. You can add music later in the editor.'
                    : 'Your voiceover plays clean over the visuals. You can drop a track in later from the editor.'}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </Section>

                <hr className='border-gray-200/70 dark:border-white/[0.06]'/>

                {/* Advanced Settings — collapsed by default. Holds power-user
                knobs that most users won't touch (image model picker for
                image-based scenes / first-frame i2i in video-based). */}
                <section>
                  <button type='button' onClick={() => setShowAdvanced((v) => !v)} className='group flex w-full items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 text-left transition-colors hover:border-gray-300 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/20' aria-expanded={showAdvanced}>
                    <span className='flex items-center gap-3'>
                      <span className='flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400'>
                        <AdjustmentsIcon className='h-3.5 w-3.5' aria-hidden/>
                      </span>
                      <span className='flex flex-col leading-tight'>
                        <span className='text-[14px] font-semibold text-gray-950 dark:text-white'>
                          Advanced
                        </span>
                        <span className='text-[12px] text-gray-500 dark:text-gray-400'>
                          {isExplainer
                ? 'Language, captions'
                : 'Language, image model, captions'}
                        </span>
                      </span>
                    </span>
                    <svg className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} viewBox='0 0 20 20' fill='currentColor'>
                      <path fillRule='evenodd' d='M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.24 4.38a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z' clipRule='evenodd'/>
                    </svg>
                  </button>

                  {showAdvanced && (<div className='mt-4 space-y-5'>
                      <div>
                        <FieldLabel>Language</FieldLabel>
                        <p className='mb-2 text-[11px] text-gray-500 dark:text-gray-400'>
                          {isExplainer
                    ? 'Language for the narration and the boards. Auto follows your prompt.'
                    : 'Language for the narration, voiceover, and captions. Auto follows your prompt.'}
                        </p>
                        <Select label='Language' value={language} onChange={setLanguage} options={[
                    { value: AUTO_VIDEO_LANGUAGE, label: 'Auto' },
                    ...VIDEO_LANGUAGE_OPTIONS.filter(
                    // Only the languages the boards have fonts for.
                    (opt) => !isExplainer || isExplainerLanguage(opt.code)).map((opt) => ({
                        value: opt.code,
                        label: opt.label,
                    })),
                ]} className='max-w-xs'/>
                      </div>

                      {!isExplainer && (<div>
                          <FieldLabel>Image model</FieldLabel>
                          <p className='mb-2 text-[11px] text-gray-500 dark:text-gray-400'>
                            Used for image-based scenes and the first-frame
                            reference in video-based generations.
                          </p>
                          <div className='grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-3'>
                            {IMAGE_MODELS.map((m) => {
                        const active = imageModel === m.id;
                        const Icon = m.icon;
                        return (<button key={m.id} type='button' onClick={() => setImageModel(m.id)} className={`group relative flex flex-col rounded-xl border bg-white px-3 py-2.5 text-left transition-all dark:bg-white/[0.03] ${active
                                ? 'border-primary-500 shadow-[0_0_0_3px_rgba(99,102,241,0.15)]'
                                : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20'}`} aria-pressed={active} title={m.description}>
                                  <span className={`flex items-center gap-1.5 text-sm font-medium ${active
                                ? 'text-primary-700 dark:text-primary-400'
                                : 'text-gray-900 dark:text-gray-100'}`}>
                                    {Icon && (<Icon className='h-3.5 w-3.5 shrink-0'/>)}
                                    {m.label}
                                  </span>
                                  <span className='mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400'>
                                    {m.description}
                                  </span>
                                </button>);
                    })}
                          </div>
                        </div>)}

                      <div>
                        <div className='mb-3 flex items-center justify-between'>
                          <div>
                            <FieldLabel>Captions</FieldLabel>
                            <p className='text-[11px] text-gray-500 dark:text-gray-400'>
                              {isExplainer
                    ? 'Off by default: the boards already show the key words. You can turn them on later in the editor.'
                    : 'On-screen captions synced with the voiceover.'}
                            </p>
                          </div>
                          <button type='button' role='switch' aria-checked={captionsEnabled} onClick={() => setCaptionsEnabled((v) => !v)} className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${captionsEnabled
                    ? 'bg-primary-500'
                    : 'bg-gray-300 dark:bg-white/20'}`}>
                            <span aria-hidden='true' className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${captionsEnabled
                    ? 'translate-x-5'
                    : 'translate-x-0'}`}/>
                          </button>
                        </div>
                      </div>

                      <div className={captionsEnabled
                    ? ''
                    : 'pointer-events-none opacity-40 select-none'}>
                        <FieldLabel>Caption style</FieldLabel>
                        <p className='mb-2 text-[11px] text-gray-500 dark:text-gray-400'>
                          Applied to the on-screen captions. You can switch it
                          later in the editor.
                        </p>
                        <div className='grid grid-cols-2 gap-2 sm:grid-cols-3'>
                          {CAPTION_PRESET_OPTIONS.map((opt) => {
                    const active = captionPreset === opt.id;
                    return (<button key={opt.id} type='button' onClick={() => setCaptionPreset(opt.id)} disabled={!captionsEnabled} className={`group relative flex flex-col rounded-xl border bg-white px-3 py-2.5 text-left transition-all dark:bg-white/[0.03] ${active
                            ? 'border-primary-500 shadow-[0_0_0_3px_rgba(99,102,241,0.15)]'
                            : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20'}`} aria-pressed={active} title={opt.description}>
                                <div className='mb-2 flex h-10 items-center justify-center rounded-md bg-gradient-to-br from-gray-700 to-gray-900' aria-hidden>
                                  <span style={{
                            fontFamily: opt.preview.fontFamily,
                            fontWeight: opt.preview.fontWeight,
                            textTransform: opt.preview.uppercase
                                ? 'uppercase'
                                : 'none',
                            color: opt.preview.restColor,
                            textShadow: opt.preview.textShadow,
                            fontSize: 13,
                            letterSpacing: 0.2,
                        }}>
                                    THE{' '}
                                    <span style={{
                            color: opt.preview.highlightColor,
                            backgroundColor: opt.preview.highlightBackground,
                            padding: opt.preview.highlightBackground
                                ? '0.05em 0.3em'
                                : undefined,
                            borderRadius: opt.preview
                                .highlightBackground
                                ? '0.15em'
                                : undefined,
                        }}>
                                      WORD
                                    </span>
                                  </span>
                                </div>
                                <span className={`text-sm font-medium ${active
                            ? 'text-primary-700 dark:text-primary-400'
                            : 'text-gray-900 dark:text-gray-100'}`}>
                                  {opt.label}
                                </span>
                                <span className='mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400'>
                                  {opt.description}
                                </span>
                              </button>);
                })}
                        </div>
                      </div>
                    </div>)}
                </section>

                {/* Main CTA. Lives at the bottom of the left column on
                desktop so the user scrolls past every option before
                reaching it. Mobile uses the sticky bottom bar instead. */}
                <div className='hidden lg:block'>
                  <button onClick={handleGenerate} disabled={!prompt.trim() || mediaUploading || referenceUploading} className='group flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-5 py-3.5 text-[14px] font-semibold text-white shadow-[0_1px_2px_rgba(15,15,25,0.08),0_0_0_1px_rgba(99,102,241,0.4),inset_0_1px_0_rgba(255,255,255,0.18)] transition-all hover:bg-primary-600 hover:shadow-[0_2px_8px_rgba(99,102,241,0.25),0_0_0_1px_rgba(99,102,241,0.5),inset_0_1px_0_rgba(255,255,255,0.18)] active:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none dark:disabled:bg-white/10 dark:disabled:text-gray-500'>
                    {prompt.trim() &&
                !mediaUploading &&
                !referenceUploading && (<svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor' aria-hidden>
                          <path d='M11.3 1.1a.75.75 0 011.18.85L10.9 7.5h4.85a.75.75 0 01.57 1.24l-8.6 10a.75.75 0 01-1.3-.66l1.58-5.58H3.15a.75.75 0 01-.57-1.24l8.72-10z'/>
                        </svg>)}
                    {mediaUploading || referenceUploading
                ? 'Uploading media…'
                : prompt.trim()
                    ? 'Generate video'
                    : 'Add a sentence to start'}
                  </button>
                  {isExplainer && (<p className='mt-2.5 text-center text-[12px] font-medium text-gray-600 dark:text-gray-300'>
                      <ExplainerCostNote estimate={explainerEstimate}/>
                    </p>)}
                  <p className='mt-2.5 text-center text-[11px] text-gray-400'>
                    {isExplainer
                ? `Usually takes about ${explainerGenerationMinutes(autoDuration
                    ? EXPLAINER_DURATION.autoMax
                    : shownDuration)} min.`
                : 'Usually takes 2–5 minutes.'}{' '}
                    We&apos;ll save the project for you.
                  </p>
                </div>
              </div>

              {/* Right column: live summary preview. Hidden on mobile (the
                sticky bottom bar carries the CTA there). */}
              <aside className='hidden lg:sticky lg:top-6 lg:block lg:self-start'>
                <PreviewPanel aspectRatio={aspectRatio} stylePreview={stylePreview} styleLabel={isExplainer
                ? selectedExplainerLook.label
                : selectedStyle?.label || 'Default'} styleThumbnail={isExplainer ? undefined : selectedStyle?.thumbnail} 
        // The explainer clip is in the Classic look, so the other
        // looks show their own still instead.
        workflowPreviewVideo={isExplainer && explainerLook !== DEFAULT_EXPLAINER_LOOK
                ? undefined
                : selectedWorkflow?.previewVideo} previewPoster={isExplainer
                ? selectedExplainerLook.image
                : selectedWorkflow?.previewPoster} duration={shownDuration} workflowType={submittedWorkflowType} workflowLabel={isShort
                ? `${selectedWorkflow?.label || 'Explainer'} Short`
                : selectedWorkflow?.label || 'Image story'} voice={selectedVoice} musicLabel={musicSummaryLabel} actor={isCharacterFlow ? selectedActor : undefined} noVoice={noVoice} noMusic={noMusic}/>
              </aside>
            </div>)}
        </div>
      </div>

      {/* Mobile sticky CTA — always-in-reach Generate button on small screens.
            Desktop uses the right-rail button instead. Hidden during generation
            since the page already shows progress and a cancel via abort. */}
      {!isGenerating && (<div className='fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/90 px-4 py-3 backdrop-blur-md lg:hidden dark:border-white/10 dark:bg-[#0b0b10]/85' style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          {isExplainer && (<p className='mb-2 text-center text-[11px] font-medium text-gray-600 dark:text-gray-300'>
              <ExplainerCostNote estimate={explainerEstimate}/>
            </p>)}
          <button onClick={handleGenerate} disabled={!prompt.trim() || mediaUploading || referenceUploading} className='flex w-full items-center justify-center gap-2 rounded-xl bg-primary-500 px-5 py-3 text-[14px] font-semibold text-white shadow-[0_1px_2px_rgba(15,15,25,0.08),inset_0_1px_0_rgba(255,255,255,0.18)] transition-colors hover:bg-primary-600 active:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 dark:disabled:bg-white/10 dark:disabled:text-gray-500'>
            {prompt.trim() && !mediaUploading && !referenceUploading && (<svg className='h-4 w-4' viewBox='0 0 20 20' fill='currentColor' aria-hidden>
                <path d='M11.3 1.1a.75.75 0 011.18.85L10.9 7.5h4.85a.75.75 0 01.57 1.24l-8.6 10a.75.75 0 01-1.3-.66l1.58-5.58H3.15a.75.75 0 01-.57-1.24l8.72-10z'/>
              </svg>)}
            {mediaUploading || referenceUploading
                ? 'Uploading media…'
                : prompt.trim()
                    ? 'Generate video'
                    : 'Add a sentence to start'}
          </button>
        </div>)}

      <AuthModal showModal={showAuth} onClose={() => setShowAuth(false)} redirectTo='/create-video'/>

      {shouldUpgrade && (<PricingModal isVisible={shouldUpgrade} message='AI video scenes are available on paid plans. On the free plan you can still create videos from AI images, real stock footage, or your own photos.' onClose={() => setShouldUpgrade(false)}/>)}

      <Modal showModal={showActorModal} onClose={() => setShowActorModal(false)} className='overflow-auto sm:max-w-6xl'>
        <ActorSelectorModal actors={[]} selectedActor={selectedActor || null} voices={modalVoices} onSelect={handleActorSelected} onClose={() => setShowActorModal(false)}/>
      </Modal>

      <style jsx global>{`
        @keyframes cv-fade-in {
          from {
            opacity: 0;
            transform: translateY(6px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .cv-fade-in {
          animation: cv-fade-in 0.4s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes cv-shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(400%);
          }
        }
        .cv-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 100%;
          height: 4px;
          background: linear-gradient(
            to right,
            rgb(var(--tw-color-primary-500)) 0%,
            rgb(var(--tw-color-primary-500)) var(--cv-fill, 50%),
            rgba(99, 102, 241, 0.15) var(--cv-fill, 50%),
            rgba(99, 102, 241, 0.15) 100%
          );
          border-radius: 9999px;
          outline: none;
          cursor: pointer;
        }
        .cv-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 9999px;
          background: white;
          border: 2px solid rgb(var(--tw-color-primary-500));
          box-shadow: 0 1px 3px rgba(99, 102, 241, 0.25);
          cursor: grab;
          transition: transform 0.15s ease-out;
        }
        .cv-slider::-webkit-slider-thumb:active {
          cursor: grabbing;
          transform: scale(1.1);
        }
        .cv-slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border-radius: 9999px;
          background: white;
          border: 2px solid rgb(var(--tw-color-primary-500));
          box-shadow: 0 1px 3px rgba(99, 102, 241, 0.25);
          cursor: grab;
        }
      `}</style>
    </NarrowDashboardLayout>);
}
// ── Sub-components ──────────────────────────────────────────────
function Section({ number, title, hint, trailing, children, }: {
    number?: string;
    title: string;
    hint?: string;
    trailing?: React.ReactNode;
    children: React.ReactNode;
}) {
    return (<section>
      <div className='mb-4 flex items-baseline justify-between gap-3'>
        <div className='flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5'>
          {number && (<span className='font-mono text-[11px] tabular-nums text-gray-400 dark:text-gray-500'>
              {number}
            </span>)}
          <h2 className='text-[15px] font-semibold text-gray-950 dark:text-white'>
            {title}
          </h2>
          {hint && (<span className='text-[13px] text-gray-500 dark:text-gray-400'>
              {hint}
            </span>)}
        </div>
        {trailing && <div className='flex-shrink-0'>{trailing}</div>}
      </div>
      {children}
    </section>);
}
function FieldLabel({ children }: {
    children: React.ReactNode;
}) {
    return (<label className='mb-2 block text-xs font-medium text-gray-700 dark:text-gray-300'>
      {children}
    </label>);
}
// Tiny switch-shaped toggle styled to match the existing aria-pressed
// pattern. Used inside the 'your-own-media' panel for the three behaviour
// toggles (use-only-provided / smart-crop / turn-images-into-videos).
function YourOwnMediaToggle({ checked, onChange, title, description, }: {
    checked: boolean;
    onChange: (next: boolean) => void;
    title: string;
    description: string;
}) {
    return (<button type='button' onClick={() => onChange(!checked)} aria-pressed={checked} className='flex w-full items-start gap-3 rounded-md border border-transparent bg-white p-2 text-left transition-colors hover:border-gray-200 dark:bg-white/[0.02] dark:hover:border-white/10'>
      <span className={`mt-0.5 inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-primary-500' : 'bg-gray-200 dark:bg-white/10'}`} aria-hidden>
        <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`}/>
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block text-[13px] font-medium text-gray-900 dark:text-gray-100'>
          {title}
        </span>
        <span className='mt-0.5 block text-[11px] leading-tight text-gray-500 dark:text-gray-400'>
          {description}
        </span>
      </span>
    </button>);
}
// Inline picker for the user's own custom actors. Multi-select: click any
// card to add it to the selection (or click again to remove). Image models
// accept multiple reference photos so you can blend characters into the
// same video. The "+ New" tile opens the existing create-actor flow.
function CharacterPicker({ actors, selectedActorIds, onToggle, onOpenCreate, }: {
    actors: CustomActor[];
    selectedActorIds: string[];
    onToggle: (actor: CustomActor) => void;
    onOpenCreate: () => void;
}) {
    const hasActors = actors.length > 0;
    const selectedCount = selectedActorIds.length;
    return (<div className='mt-5 border-t border-gray-100 pt-5 dark:border-white/5'>
      <div className='mb-2 flex items-baseline justify-between gap-2'>
        <FieldLabel>
          Characters{' '}
          <span className='font-normal text-gray-400 dark:text-gray-500'>
            (optional{selectedCount > 0 ? ` · ${selectedCount} selected` : ''})
          </span>
        </FieldLabel>
        {hasActors && (<span className='text-[11px] text-gray-500 dark:text-gray-400'>
            Pick one or more — every selected face is a reference for the scene
            images.
          </span>)}
      </div>

      {hasActors ? (<div className='flex flex-wrap gap-2'>
          {actors.map((actor) => {
                const active = selectedActorIds.includes(actor.id);
                return (<button key={actor.id} type='button' onClick={() => onToggle(actor)} aria-pressed={active} className='group flex w-[88px] flex-col items-center gap-1.5 text-center' title={active ? `${actor.name} (click to remove)` : actor.name}>
                {/* Outer wrapper holds the checkmark badge so it isn't
                        clipped by the photo's circular overflow-hidden mask. */}
                <div className='relative'>
                  <div className={`h-16 w-16 overflow-hidden rounded-full bg-gradient-to-br from-gray-200 to-gray-300 transition-shadow dark:from-gray-700 dark:to-gray-800 ${active
                        ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-[#fafafb] dark:ring-offset-[#0b0b10]'
                        : 'ring-1 ring-inset ring-gray-200 group-hover:ring-gray-300 dark:ring-white/10 dark:group-hover:ring-white/25'}`}>
                    {actor.photo && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={actor.photo} alt={actor.name} className='h-full w-full object-cover' onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                        }}/>)}
                  </div>
                  {active && (<span className='absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-500 text-white shadow-sm ring-2 ring-[#fafafb] dark:ring-[#0b0b10]'>
                      <svg className='h-3 w-3' viewBox='0 0 20 20' fill='currentColor'>
                        <path fillRule='evenodd' d='M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0l-3.5-3.5a1 1 0 011.4-1.4l2.8 2.8 6.8-6.8a1 1 0 011.4 0z' clipRule='evenodd'/>
                      </svg>
                    </span>)}
                </div>
                <span className={`block w-full truncate text-[12px] font-medium leading-tight ${active
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300'}`}>
                  {actor.name}
                </span>
              </button>);
            })}
          <button type='button' onClick={onOpenCreate} className='group flex w-[88px] flex-col items-center gap-1.5 text-center' title='Create a new character'>
            <div className='flex h-16 w-16 items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-400 transition-colors group-hover:border-primary-400 group-hover:text-primary-500 dark:border-white/15 dark:text-gray-500 dark:group-hover:border-primary-400 dark:group-hover:text-primary-300'>
              <svg className='h-5 w-5' viewBox='0 0 20 20' fill='currentColor'>
                <path d='M10 4a1 1 0 011 1v4h4a1 1 0 110 2h-4v4a1 1 0 11-2 0v-4H5a1 1 0 110-2h4V5a1 1 0 011-1z'/>
              </svg>
            </div>
            <span className='block w-full truncate text-[12px] font-medium leading-tight text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200'>
              New
            </span>
          </button>
        </div>) : (<button type='button' onClick={onOpenCreate} className='group flex w-full items-center gap-3 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-3 text-left transition-colors hover:border-primary-400 hover:bg-primary-50/30 dark:border-white/15 dark:bg-white/[0.03] dark:hover:border-primary-400 dark:hover:bg-primary-500/[0.06]'>
          <span className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400 group-hover:bg-primary-100 group-hover:text-primary-600 dark:bg-white/10 dark:text-gray-500 dark:group-hover:bg-primary-500/15 dark:group-hover:text-primary-300'>
            <svg className='h-5 w-5' viewBox='0 0 20 20' fill='currentColor'>
              <path d='M10 4a1 1 0 011 1v4h4a1 1 0 110 2h-4v4a1 1 0 11-2 0v-4H5a1 1 0 110-2h4V5a1 1 0 011-1z'/>
            </svg>
          </span>
          <span className='min-w-0 flex-1'>
            <span className='block text-[13px] font-medium text-gray-900 dark:text-gray-100'>
              Create your first character
            </span>
            <span className='block text-[11px] text-gray-500 dark:text-gray-400'>
              Lock the same face and voice across every scene of your videos.
            </span>
          </span>
          <span className='text-[11px] font-medium text-primary-600 dark:text-primary-400'>
            Create →
          </span>
        </button>)}
    </div>);
}
// ── Summary panel ──────────────────────────────────────────────
// Information-only — not a fake player. Shows the configured aspect/style as
// a calm preview frame with the actual style thumbnail when available, plus a
// compact spec list. No play button, no fake progress, no fake timecode —
// nothing that promises an interaction the user won't get here.
interface PreviewPanelProps {
    aspectRatio: AspectRatio;
    stylePreview: {
        bg: string;
        overlay?: string;
        tone: 'light' | 'dark';
    };
    styleLabel: string;
    styleThumbnail?: string;
    workflowPreviewVideo?: string;
    /** 16:9 still: the video's poster, or the frame itself without a video. */
    previewPoster?: string;
    duration: number;
    workflowType: WorkflowType;
    workflowLabel: string;
    voice: Voice | undefined;
    musicLabel: string;
    actor?: Actor;
    noVoice?: boolean;
    noMusic?: boolean;
    generating?: boolean;
}
function PreviewPanel({ aspectRatio, stylePreview, styleLabel, styleThumbnail, workflowPreviewVideo, previewPoster, duration, workflowType, workflowLabel, voice, musicLabel, actor, noVoice = false, noMusic = false, generating = false, }: PreviewPanelProps) {
    // The workflow preview videos (and their posters) are all 16:9 for now, so
    // always frame them in 16:9 regardless of the selected aspect ratio —
    // otherwise object-cover crops them hard into a 9:16 / 1:1 box. (Later:
    // 9:16 preview clips, then this can follow aspectRatio.) The style
    // thumbnail / gradient fallback still uses the selected aspect so the
    // chosen format reads correctly.
    const aspectClass = workflowPreviewVideo || previewPoster
        ? 'aspect-video'
        : aspectRatio === '9:16'
            ? 'aspect-[9/16] max-h-[280px] mx-auto'
            : aspectRatio === '1:1'
                ? 'aspect-square max-h-[280px] mx-auto'
                : 'aspect-video';
    const visualsValue = workflowType === 'video-story'
        ? 'AI video clips'
        : workflowType === 'motion-graphics'
            ? 'Graphics + text'
            : workflowType === 'stock-videos'
                ? 'Stock footage'
                : workflowType === 'talking-video'
                    ? 'Character clips'
                    : workflowType === 'explainer'
                        ? 'Animated explainer'
                        : 'AI image story';
    return (<div className='overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(15,15,25,0.04)] dark:border-white/10 dark:bg-white/[0.03]'>
      <div className='flex items-baseline justify-between px-4 pb-3 pt-3.5'>
        <span className='text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400'>
          Brief
        </span>
        <span className='font-mono text-[11px] tabular-nums text-gray-400 dark:text-gray-500'>
          {aspectRatio}
          <span className='mx-1 text-gray-300 dark:text-gray-600'>·</span>
          {workflowType === 'explainer'
            ? formatExplainerDuration(duration)
            : `${duration}s`}
        </span>
      </div>

      {/* Aspect frame — shows the workflow preview video when present,
            otherwise the style thumbnail or the style's preview gradient. Calm,
            not theatrical. */}
      <div className='relative w-full px-4'>
        <div className={`relative ${aspectClass} overflow-hidden rounded-xl border border-black/5 dark:border-white/5`} style={workflowPreviewVideo || previewPoster || styleThumbnail
            ? undefined
            : { background: stylePreview.bg }}>
          {workflowPreviewVideo ? (<video key={workflowPreviewVideo} src={workflowPreviewVideo} poster={previewPoster} autoPlay loop muted playsInline preload='metadata' aria-hidden className='absolute inset-0 h-full w-full object-cover'/>) : previewPoster ? (<Image src={previewPoster} alt='' fill sizes='400px' className='object-cover'/>) : styleThumbnail ? (<Image src={styleThumbnail} alt='' fill sizes='400px' className='object-cover'/>) : (stylePreview.overlay && (<div className='absolute inset-0' style={{ background: stylePreview.overlay }}/>))}
          {generating && (<div className='absolute inset-x-0 top-0 h-0.5 overflow-hidden'>
              <div className='h-full w-1/3 bg-primary-500/80 [animation:cv-shimmer_1.6s_linear_infinite]'/>
            </div>)}
          {/* Faux timecode in the corner — mirrors the design's pre-play
            indicator. Not a real player, just a visual cue. */}
          <div className='pointer-events-none absolute left-2 top-2 inline-flex items-center gap-1 rounded-md bg-black/35 px-1.5 py-0.5 text-[9px] font-medium text-white backdrop-blur-sm'>
            <span className='inline-block h-1.5 w-1.5 rounded-full bg-red-500'/>
            <span className='font-mono tabular-nums'>
              {`0:00 / ${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}`}
            </span>
          </div>
          <div className={`pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-between px-3 py-2 text-[10px] font-medium ${stylePreview.tone === 'dark' ||
            styleThumbnail ||
            workflowPreviewVideo ||
            previewPoster
            ? 'text-white'
            : 'text-gray-800'}`} style={{
            background: 'linear-gradient(0deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 60%)',
        }}>
            <span className='truncate'>{styleLabel}</span>
            <span className='truncate'>{workflowLabel}</span>
          </div>
        </div>
      </div>

      {/* Compact spec list */}
      <dl className='space-y-3 px-4 pb-4 pt-4 text-[12px]'>
        <SummaryRow label='Visuals'>
          <div className='flex flex-col items-end leading-tight'>
            <span className='truncate text-[12px] font-medium text-gray-900 dark:text-gray-100'>
              {visualsValue}
            </span>
            <span className='truncate text-[11px] font-normal text-gray-500 dark:text-gray-400'>
              {styleLabel}
            </span>
          </div>
        </SummaryRow>
        <SummaryRow label={workflowType === 'talking-video' ? 'Character' : 'Voice'}>
          <div className='flex items-center gap-2'>
            {workflowType === 'talking-video' && actor ? (<>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={actor.photo} alt='' className='h-5 w-5 rounded-full object-cover'/>
                <div className='flex flex-col items-end leading-tight'>
                  <span className='truncate text-[12px] font-medium text-gray-900 dark:text-gray-100'>
                    {actor.name}
                  </span>
                </div>
              </>) : noVoice ? (<span className='truncate text-gray-500 dark:text-gray-400'>
                Off
              </span>) : (<>
                {voice?.image && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={getVoiceImageUrl(voice.image)} alt='' className='h-5 w-5 rounded-full object-cover'/>)}
                <div className='flex flex-col items-end leading-tight'>
                  <span className='truncate text-[12px] font-medium text-gray-900 dark:text-gray-100'>
                    {voice?.name || '—'}
                  </span>
                  {voice?.accent && (<span className='truncate text-[11px] font-normal text-gray-500 dark:text-gray-400'>
                      {voice.accent}
                    </span>)}
                </div>
              </>)}
          </div>
        </SummaryRow>
        <SummaryRow label='Music'>
          {noMusic ? (<span className='truncate text-gray-500 dark:text-gray-400'>
              Off
            </span>) : (<span className='truncate'>{musicLabel}</span>)}
        </SummaryRow>
      </dl>
    </div>);
}
/**
 * What an Explainer costs: the quote on paid plans (charged in full, or less
 * when the video comes out shorter), the free try otherwise.
 */
/** An Explainer's kind (a video or a Short) and, for a Short, its style. */
function ExplainerFormatPicker({ format, onFormat, shortStyle, onShortStyle, }: {
    format: ExplainerFormat;
    onFormat: (format: ExplainerFormat) => void;
    shortStyle: ExplainerShortStyle;
    onShortStyle: (style: ExplainerShortStyle) => void;
}) {
    const kinds: {
        id: ExplainerFormat;
        label: string;
        hint: string;
    }[] = [
        { id: 'video', label: 'Video', hint: 'Up to 10 minutes, with chapters' },
        { id: 'short', label: 'Short', hint: 'Vertical, 20-60 seconds' },
    ];
    return (<div className='md:col-span-2'>
      <FieldLabel>Kind</FieldLabel>
      <div className='inline-flex gap-1.5'>
        {kinds.map((kind) => {
            const active = format === kind.id;
            return (<button key={kind.id} type='button' onClick={() => onFormat(kind.id)} aria-pressed={active} className={`rounded-lg border px-3 py-1.5 text-left transition-colors ${active
                    ? 'border-primary-500 bg-primary-50/60 shadow-[0_0_0_2px_rgba(99,102,241,0.12)] dark:border-primary-400 dark:bg-primary-500/10'
                    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-white/10 dark:bg-white/[0.03] dark:hover:border-white/20'}`}>
              <div className={`text-[12px] font-medium ${active
                    ? 'text-primary-700 dark:text-primary-300'
                    : 'text-gray-800 dark:text-gray-200'}`}>
                {kind.label}
              </div>
              <div className='text-[11px] text-gray-500 dark:text-gray-400'>
                {kind.hint}
              </div>
            </button>);
        })}
      </div>
      {format === 'short' && (<div className='mt-4'>
          <FieldLabel>Short style</FieldLabel>
          <div className='flex flex-wrap gap-1.5'>
            {EXPLAINER_SHORT_STYLES.map((style) => {
                const active = shortStyle === style.id;
                return (<button key={style.id} type='button' onClick={() => onShortStyle(style.id)} aria-pressed={active} title={style.description} className={`rounded-full border px-3 py-1 text-[12px] font-medium transition-colors ${active
                        ? 'border-primary-500 bg-primary-50/60 text-primary-700 dark:border-primary-400 dark:bg-primary-500/10 dark:text-primary-300'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-white/10 dark:bg-white/[0.03] dark:text-gray-300 dark:hover:border-white/20'}`}>
                  {style.label}
                </button>);
            })}
          </div>
          <p className='mt-1.5 text-[11px] text-gray-500 dark:text-gray-400'>
            {EXPLAINER_SHORT_STYLES.find((s) => s.id === shortStyle)
                ?.description ?? ''}
            . It opens on a hook, and the boards move faster.
          </p>
        </div>)}
    </div>);
}
function ExplainerCostNote({ estimate }: {
    estimate: number | null;
}) {
    if (estimate === null) {
        return <>Free · up to {EXPLAINER_DURATION.free} s</>;
    }
    return <>Up to {estimate} credits</>;
}
function SummaryRow({ label, children, }: {
    label: string;
    children: React.ReactNode;
}) {
    return (<div className='flex items-center justify-between gap-3'>
      <dt className='text-gray-500 dark:text-gray-400'>{label}</dt>
      <dd className='min-w-0 text-right font-medium text-gray-900 dark:text-gray-100'>
        {children}
      </dd>
    </div>);
}

    async function __Next_Translate__getStaticProps__1a11b03d54a__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      
      return {
        
        
        props: {
          
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/create-video',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a11b03d54a__ as getStaticProps }
  